import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useConnectPolicy } from "@/api/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
    Activity, Captions as CaptionsIcon, Check, Lock, ChevronDown, Maximize, Minimize2, Circle, Clock, Crown, Hand, LayoutGrid, Link2, Loader2, MessageSquare, Mic, MicOff, MonitorUp,
    MoreHorizontal, NotebookPen, PencilLine, PhoneOff, PictureInPicture2, Pin, Send, Settings, Smile, Sparkles, Square, UserPlus, Users, Video, VideoOff, VolumeX, Waves, X,
    AppWindow, RefreshCw, SignalHigh, SignalLow, Volume2, WifiOff
} from "@fg/icons";
import { HubConnectionState } from "@microsoft/signalr";
import { cn } from "@/lib/utils";
import { MeetingsEvents, useMeetingsHub, type UseMeetingsHub } from "@/api/useMeetingsHub";
import {
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { CallRoster, type RosterPerson } from "./call/CallRoster";
import { OutgoingCallScreen, type OutgoingPhase } from "./call/OutgoingCallScreen";
import { DeviceSettingsDialog, type DeviceKind } from "./call/DeviceSettingsDialog";
import { useAccessToken } from "@/auth/useAccessToken";
import { connectParityApi, normalizeUserId, type MeetingChatMessage } from "@/api/connectApi";
import { MeetingWhiteboard } from "./whiteboard/MeetingWhiteboard";
import { MeetingNotes } from "./meeting/MeetingNotes";
import { CaptionsOverlay } from "./CaptionsOverlay";
import { BackgroundFilter } from "./BackgroundFilter";
import { NoiseSuppressor, readNoisePref, writeNoisePref } from "./NoiseSuppressor";
import { CallStatsPanel } from "./CallStats";
import { ConnectionQualityIndicator } from "./ConnectionQuality";
import { AudioLevelMeter } from "./AudioLevelMeter";
import { playMessageTone } from "@fg/ui-theme";
import { recordingSaver } from "./recording/recordingSaver";
import { RecordingCard, useMeetingRecordings } from "./recording/MeetingRecordings";
import { MeetingChatThread } from "./meeting/MeetingChatThread";
import { BreakoutController, OPEN_BREAKOUT_EVENT } from "./meeting/BreakoutController";
import { MessageBody } from "./files/MessageBody";
import { usePeerHealth, type PeerHealth } from "./meeting/usePeerHealth";
import { MeetingPopout, isDocumentPipSupported, openDocumentPip, type PopoutTile } from "./meeting/MeetingPopout";
import { randomToken } from "./meeting/guestIdentity";
import { enterCall } from "@/lib/activeCall";
import { cameraEncodingFor, maySendVideo, withOpusDtx, type CameraEncoding } from "./meeting/videoPolicy";
import { useSpeakingReporter, useUploadLog } from "./meeting/useSpeakerRotation";

// ── Codec + bitrate helpers ────────────────────────────────────────────────

/**
 * Prefer VP8 and H264 over VP9/AV1: both have hardware acceleration on most devices, VP9/AV1 are
 * CPU-intensive and drop frames in gallery calls. Done with the transceivers' codec preferences rather
 * than by rewriting the SDP, because negotiation now uses the implicit setLocalDescription().
 */
function preferCodecs(pc: RTCPeerConnection): void {
    const caps = typeof RTCRtpReceiver !== "undefined" && typeof RTCRtpReceiver.getCapabilities === "function"
        ? RTCRtpReceiver.getCapabilities("video")
        : null;
    if (!caps) return;
    const rank = (c: { mimeType: string }) => {
        const m = c.mimeType.toLowerCase();
        return m === "video/vp8" ? 0 : m === "video/h264" ? 1 : 2;
    };
    const sorted = [...caps.codecs].sort((a, b) => rank(a) - rank(b));
    for (const t of pc.getTransceivers()) {
        if (t.receiver.track.kind !== "video" || t.currentDirection === "stopped") continue;
        try { t.setCodecPreferences(sorted); } catch { /* not supported by this browser */ }
    }
}

/**
 * Per-sender caps: the camera by meeting size (`cameraEncodingFor` — 1.5 Mbps in a 1:1 call down to 150 kbps
 * and a quarter of the resolution past ten people; 0.4 Mbps while you present), screen 2.5 Mbps with resolution
 * kept (crisp text), audio 64 kbps. Applied whenever negotiation settles, when the room grows or shrinks, and
 * when the camera is rotated back in. The camera sender is recognised by identity, not by its track: while
 * this person is outside the video set it has no track at all.
 */
function applySenderCaps(pc: RTCPeerConnection, senders: { camera: RTCRtpSender | null; screen: RTCRtpSender | null }, camera: CameraEncoding): void {
    for (const sender of pc.getSenders()) {
        const isScreen = sender === senders.screen;
        const isCamera = !isScreen && sender === senders.camera;
        const kind = isScreen || isCamera ? "video" : sender.track?.kind;
        if (!kind) continue;
        const maxBitrate = kind === "audio" ? 64_000 : isScreen ? 2_500_000 : camera.maxBitrate;
        const params = sender.getParameters();
        // Not negotiated yet: setParameters would reject; caps are applied once it settles.
        if (!params.encodings || params.encodings.length === 0) continue;
        let changed = false;
        for (const enc of params.encodings) {
            if (enc.maxBitrate !== maxBitrate) {
                enc.maxBitrate = maxBitrate;
                changed = true;
            }
            if (isCamera && enc.scaleResolutionDownBy !== camera.scaleResolutionDownBy) {
                enc.scaleResolutionDownBy = camera.scaleResolutionDownBy;
                changed = true;
            }
        }
        if (kind === "video") {
            const pref: RTCDegradationPreference = isScreen ? "maintain-resolution" : "balanced";
            if (params.degradationPreference !== pref) {
                params.degradationPreference = pref;
                changed = true;
            }
        }
        if (changed) sender.setParameters(params).catch(() => { /* browser may reject on certain states */ });
    }
}

type LayoutMode = "gallery" | "spotlight" | "focus" | "together";

/** Together mode scenes: a backdrop the circular video tiles sit in. Chosen per viewer. */
const TOGETHER_SCENES = {
    auditorium: { label: "Auditorium", bg: "radial-gradient(ellipse at 50% 0%, #4a2f6b 0%, #2a1840 55%, #140a22 100%)" },
    coffee: { label: "Coffee shop", bg: "linear-gradient(180deg, #5b3b26 0%, #3a2418 60%, #22140d 100%)" },
    conference: { label: "Conference room", bg: "linear-gradient(180deg, #2c4a63 0%, #1d3347 60%, #101e2b 100%)" }
} as const;
type TogetherScene = keyof typeof TOGETHER_SCENES;
const readScene = (): TogetherScene => {
    try {
        const v = window.localStorage.getItem("fg.meeting.togetherScene");
        if (v && v in TOGETHER_SCENES) return v as TogetherScene;
    } catch { /* default */ }
    return "auditorium";
};

/**
 * ICE server set fed to every RTCPeerConnection. Two layers:
 *   1. Default STUN-only pool — the same Google STUN servers Teams /
 *      Meet ship as fallback. Enough for ~85 % of clients.
 *   2. VITE_TURN_SERVERS, populated at build time from the deployment
 *      env. Symmetric-NAT clients (corporate firewalls, mobile carriers
 *      behind CGNAT) need a TURN relay or the meeting silent-fails.
 *
 * Format of VITE_TURN_SERVERS is a JSON array literal so a deployment
 * can supply credentials, multiple URIs, and the transport flavour in
 * one shot:
 *   VITE_TURN_SERVERS='[{"urls":"turn:turn.fowlguard.com:3478?transport=udp","username":"…","credential":"…"}]'
 *
 * Parsing failures fall back to the STUN pool — a deployment that
 * mis-configured the env still gets calls working for non-symmetric
 * peers, and the browser console carries the parse error.
 */
const ICE_SERVERS: RTCIceServer[] = (() => {
    const stun: RTCIceServer[] = [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" }
    ];
    const raw = import.meta.env.VITE_TURN_SERVERS;
    if (typeof raw !== "string" || raw.trim().length === 0) return stun;
    try {
        const parsed = JSON.parse(raw) as RTCIceServer[];
        if (Array.isArray(parsed) && parsed.length > 0) {
            return [...stun, ...parsed];
        }
    } catch (e) {
        console.warn("VITE_TURN_SERVERS parse failed; falling back to STUN-only", e);
    }
    return stun;
})();

/** Speaker picked on the pre-join screen or in Device settings (HTMLMediaElement.setSinkId). */
const SpeakerContext = createContext<string | undefined>(undefined);

function applySink(el: HTMLMediaElement | null, sinkId: string | undefined) {
    if (!el || !sinkId) return;
    const withSink = el as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> };
    void withSink.setSinkId?.(sinkId).catch(() => undefined);
}

/** Dark menu styling for the meeting's dropdowns (the app's popover colours are light). */
const MENU = "w-60 border-white/10 bg-zinc-900 text-white";
const ITEM = "cursor-pointer text-white focus:bg-white/10 focus:text-white";

interface Peer {
    connectionId: string;
    /** Their browser session in this room — the same across their hub reconnects. */
    clientId: string;
    userId: string;
    email: string;
    displayName: string;
    /** Camera + microphone. */
    stream: MediaStream | null;
    /** Their shared screen — a separate stream next to the camera (current clients). */
    screenStream: MediaStream | null;
    /** Which of their streams is the screen, as announced with the share. */
    screenStreamId: string | null;
    audio: boolean;
    video: boolean;
    handRaised: boolean;
    /** Server time the hand went up — orders the host's queue. */
    raisedAt?: string | null;
    sharing: boolean;
    /** Their connection dropped; tile and peer connection are kept for a grace period in case they return. */
    reconnecting: boolean;
}

/** Our side of the peer connection with one remote participant. */
interface PeerLink {
    pc: RTCPeerConnection;
    /** The remote's CURRENT connection id — the link is re-keyed when they reconnect. */
    remoteId: string;
    /** Random id of this RTCPeerConnection, carried on every offer/answer/candidate we send. */
    localSession: string;
    /** The remote RTCPeerConnection's id from its messages; a new one means they rebuilt theirs. */
    remoteSession: string | null;
    /** Perfect negotiation: when both sides offer at once, the polite one yields. */
    polite: boolean;
    makingOffer: boolean;
    ignoreOffer: boolean;
    /** ICE candidates that arrived before the remote description. */
    pending: RTCIceCandidateInit[];
    audioSender: RTCRtpSender | null;
    cameraSender: RTCRtpSender | null;
    screenSender: RTCRtpSender | null;
    disconnectTimer: number | null;
    closed: boolean;
    /** Tracks from a sender that attached no stream (older browsers). */
    fallbackStream: MediaStream | null;
}

/** An offer/answer as it travels through the hub: the description plus our session tags (s = sender's, t = target's). */
type SignalDesc = { type?: RTCSdpType; sdp?: string; s?: string; t?: string | null };
type SignalIce = RTCIceCandidateInit & { s?: string; t?: string | null };

interface ParticipantsPayload {
    peers: Array<{ connectionId: string; userId: string; email: string; displayName: string; clientId?: string }>;
    videoLimited?: boolean;
    maxVideoParticipants?: number;
    /** Who may send video now (FPT-7); absent in audio meetings and from older servers. */
    videoSlots?: string[] | null;
    self?: { connectionId: string; userId: string; clientId: string };
}

interface JoinedPayload {
    connectionId: string;
    userId: string;
    email: string;
    displayName: string;
    clientId?: string;
    /** The same browser session's previous connection, which the server retired. */
    replacesConnectionId?: string | null;
}

/** The peer-connection layer, rebuilt each render and called from the (memoised) hub handlers. */
interface RtcHandlers {
    onParticipants: (p: ParticipantsPayload) => void;
    /** True for someone new (vs. someone reconnecting). */
    onUserJoined: (p: JoinedPayload) => boolean;
    onUserLeft: (p: { connectionId: string; reason?: string }) => void;
    onOffer: (from: string, d: SignalDesc) => Promise<void>;
    onAnswer: (from: string, d: SignalDesc) => Promise<void>;
    onCandidate: (from: string, c: SignalIce) => Promise<void>;
    onScreenShare: (p: { connectionId: string; sharing: boolean; streamId?: string | null }) => void;
}

/** How long a dropped participant's tile and peer connection wait for them to reconnect. */
const PEER_GRACE_MS = 30_000;
/** Out of the room this long (hub down or not rejoined): offer a manual Rejoin. Retries continue meanwhile. */
const RECONNECT_TIMEOUT_MS = 120_000;
/** "Show floating window when I switch tabs" (on by default). */
const AUTO_POPOUT_KEY = "fg.connect.meeting.autoPopout";

function readAutoPopout(): boolean {
    try {
        return window.localStorage.getItem(AUTO_POPOUT_KEY) !== "0";
    } catch {
        return true;
    }
}

interface LobbyKnocker {
    connectionId: string;
    userId: string;
    email: string;
    displayName: string;
}

interface MeetingRoomProps {
    meetingId: string;
    meetingSubject: string;
    /** "audio" suppresses camera access and hides all video tiles. Default "video". */
    kind?: "audio" | "video";
    /** When supplied, lets the room derive isHost and surface host-only controls (spotlight / mute-all / force-mute / layout). */
    hostUserId?: string;
    /** Set when this room is a breakout room: the main meeting it belongs to. */
    breakoutParentId?: string | null;
    /** Scheduled end: drives the "Meeting ends in 5 minutes" notice. Nobody is removed when it passes. */
    scheduledEnd?: string | null;
    /**
     * You are placing this call: until someone answers, show the "Calling…"
     * screen (ringback, cancel, 45 s no-answer). Who waits in the lobby is
     * decided by the server now — a client-side `useLobby` flag that no page
     * ever set was all there used to be.
     */
    placingCall?: boolean;
    /** Who the call rings — "Bijo V", or "3 people". */
    calleeLabel?: string;
    /** How many people the call rings, so one decline doesn't end a group call. */
    calleeCount?: number;
    /** Speaker chosen on the pre-join screen. */
    speakerId?: string;
    /**
     * When set, the room runs in anonymous guest mode: it connects to the
     * meetings hub without a token and joins via JoinMeetingAsGuest under the
     * given display name. Host-only controls, recording, and invite are
     * hidden. Used by the public /guest/:meetingId landing page.
     */
    guest?: {
        displayName: string;
        /** The guest's stable per-meeting secret (guestIdentity.ts): the same guest across reconnects. */
        token?: string;
        initialMicOn?: boolean;
        initialCamOn?: boolean;
    };
    /**
     * Choices made on the PreJoin ("green room") screen. Signed-in users used
     * to land straight in the call with the camera live and whatever device
     * the browser happened to pick; these carry their decision in.
     */
    initialMicOn?: boolean;
    initialCamOn?: boolean;
    deviceIds?: { cameraId?: string; micId?: string };
    onLeave: () => void;
}

function escapeText(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function MeetingRoom({ meetingId, meetingSubject, kind = "video", hostUserId, breakoutParentId = null, scheduledEnd, placingCall = false, calleeLabel, calleeCount = 0, speakerId, guest, initialMicOn, initialCamOn, deviceIds, onLeave }: MeetingRoomProps) {
    const isAudio = kind === "audio";
    const isGuest = guest !== undefined;
    const auth = useAuth();
    const policy = useConnectPolicy();
    // Guests never host, never record, and never see host controls.
    const allowRecording = !isGuest && (policy.data?.allowRecording ?? true);
    const myUserId = normalizeUserId(auth.user?.profile.sub);
    // Read from the hub handlers, which are memoised once.
    const myUserIdRef = useRef(myUserId);
    myUserIdRef.current = myUserId;
    const isHost = !isGuest && hostUserId !== undefined && normalizeUserId(hostUserId) === myUserId;
    const selfDisplayName = isGuest
        ? `${guest!.displayName} (guest)`
        : (auth.user?.profile.name ?? auth.user?.profile.email ?? "You");
    const [spotlightConnectionId, setSpotlightConnectionId] = useState<string | null>(null);
    const [layoutMode, setLayoutMode] = useState<LayoutMode>("gallery");
    const [scene, setScene] = useState<TogetherScene>(readScene);
    // Personal view choices (nobody else sees them): a tile pinned big for me ("self" or a connection id), my own video hidden, full screen.
    const [pinnedTile, setPinnedTile] = useState<string | null>(null);
    const [selfHidden, setSelfHidden] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    useEffect(() => {
        const on = () => setFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener("fullscreenchange", on);
        return () => document.removeEventListener("fullscreenchange", on);
    }, []);
    const toggleFullscreen = () => {
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
        else void document.documentElement.requestFullscreen().catch(() => undefined);
    };
    // The host locked the meeting: nobody new can join. `lockedOut` is this person being turned away at the door.
    const [meetingLocked, setMeetingLocked] = useState(false);
    const [lockedOut, setLockedOut] = useState(false);
    // The tenant caps how many people one meeting holds: the person turned away for that reason is told so.
    const [meetingFull, setMeetingFull] = useState(false);
    // The meeting's recording as the hub reports it — sent to everyone (the
    // host too) and replayed to late joiners. Only the host records.
    const [roomRecording, setRoomRecording] = useState<{ byConnectionId: string; byDisplayName: string } | null>(null);
    // The "<host> started recording…" banner (and other recording notices).
    const [recordingNotice, setRecordingNotice] = useState<string | null>(null);
    const [captionsOn, setCaptionsOn] = useState(false);
    // Last 8 incoming captions from peers — display-side only.
    const [incomingCaptions, setIncomingCaptions] = useState<Array<{ id: string; speaker: string; text: string }>>([]);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    // Seed mic/cam from the guest green-room choices when present, so a guest who
    // muted/turned off their camera before joining isn't switched back on.
    // True once the getUserMedia attempt has finished, whether it produced a
    // stream or failed. Gating the room join on this (rather than on a stream)
    // is what lets a device-less participant still join and watch.
    const [mediaSettled, setMediaSettled] = useState(false);
    const [micOn, setMicOn] = useState(guest?.initialMicOn ?? initialMicOn ?? true);
    const [cameraOn, setCameraOn] = useState(guest?.initialCamOn ?? initialCamOn ?? !isAudio);
    const [handRaised, setHandRaised] = useState(false);
    const handRaisedRef = useRef(false);
    handRaisedRef.current = handRaised;
    const [sharing, setSharing] = useState(false);
    // Mirror of `sharing` readable from the memoized SignalR handlers, which close over the first
    // render's state and would otherwise always see `false`.
    const sharingRef = useRef(false);
    // Local getDisplayMedia stream while we present — used for the
    // self-preview on the share stage and to stop capture on toggle-off.
    const [shareStream, setShareStream] = useState<MediaStream | null>(null);
    const shareStreamRef = useRef<MediaStream | null>(null);
    // Browser picture-in-picture of the (single) remote peer — WhatsApp-style
    // floating video for 1:1 calls.
    const [pipOn, setPipOn] = useState(false);
    const [blurOn, setBlurOn] = useState(false);
    const [blurBusy, setBlurBusy] = useState(false);
    const blurFilterRef = useRef<BackgroundFilter | null>(null);
    const originalCamTrackRef = useRef<MediaStreamTrack | null>(null);
    const [noiseSuppressorOn, setNoiseSuppressorOn] = useState(false);
    const [noiseBusy, setNoiseBusy] = useState(false);
    const noiseSuppressorRef = useRef<NoiseSuppressor | null>(null);
    const originalMicTrackRef = useRef<MediaStreamTrack | null>(null);
    const [peers, setPeers] = useState<Record<string, Peer>>({});
    const [statsOpen, setStatsOpen] = useState(false);
    // The meeting chat is stored now. Signed-in people read it from the server
    // (history on join, the hub's events refresh it); a guest has no token and
    // sees what arrives while they're here.
    const [guestChat, setGuestChat] = useState<MeetingChatMessage[]>([]);
    const [chatOpen, setChatOpen] = useState(false);
    const chatOpenRef = useRef(false);
    chatOpenRef.current = chatOpen;
    const [chatDraft, setChatDraft] = useState("");
    // Messages from others while the chat panel was closed — the badge on the Chat button.
    const [chatUnread, setChatUnread] = useState(0);
    // Server time my hand went up (orders the queue); and "the host lowered your hand".
    const [handRaisedAt, setHandRaisedAt] = useState<string | null>(null);
    const [handNotice, setHandNotice] = useState<string | null>(null);
    // "Meeting ends in 5 minutes" — a notice only; nobody is removed.
    const [endNotice, setEndNotice] = useState<string | null>(null);
    const [reaction, setReaction] = useState<{ emoji: string; key: number; who?: string } | null>(null);
    const [mediaError, setMediaError] = useState<string | null>(null);
    const [whiteboardOpen, setWhiteboardOpen] = useState(false);
    // "<name> opened the whiteboard" — an invitation to join them on it.
    const [wbInvite, setWbInvite] = useState<string | null>(null);
    // Meeting notes side panel (signed-in people only; guests have no token).
    const [notesOpen, setNotesOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteQuery, setInviteQuery] = useState("");
    const [inviteSuggestions, setInviteSuggestions] = useState<Array<{ userId: string; email: string; displayName: string | null }>>([]);
    const [inviteStatus, setInviteStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
    const [autoEndMsg, setAutoEndMsg] = useState<string | null>(null);
    const [recording, setRecording] = useState(false);
    const [recordingBusy, setRecordingBusy] = useState(false);
    // Leaving / ending while recording asks first (the recording stops and is saved).
    const [confirmLeave, setConfirmLeave] = useState<null | "leave" | "end">(null);

    // ── Meeting lobby ────────────────────────────────────────────────────────
    // `lobbyState` drives the local user's perspective:
    //   "idle"     — not in lobby (default, or after admission)
    //   "waiting"  — knocked but not yet admitted/denied by host
    //   "denied"   — host denied entry
    // Hosts see a separate `lobbyKnockers` list and can admit/deny each.
    const [lobbyState, setLobbyState] = useState<"idle" | "waiting" | "denied">("idle");
    const [lobbyKnockers, setLobbyKnockers] = useState<LobbyKnocker[]>([]);
    const [rosterOpen, setRosterOpen] = useState(false);
    const [devicesOpen, setDevicesOpen] = useState(false);
    const [activeDevices, setActiveDevices] = useState<{ cameraId?: string; micId?: string; speakerId?: string }>(
        () => ({ cameraId: deviceIds?.cameraId, micId: deviceIds?.micId, speakerId }));
    // Past the mesh cap (peer-to-peer, no media server) the server tells a
    // newcomer to join without video.
    // Only from servers that predate speaker rotation (no videoSlots in the participant list).
    const [videoLimited, setVideoLimited] = useState(false);
    const [maxVideo, setMaxVideo] = useState(6);
    // Speaker-rotated video (FPT-7): who may send video now, as the hub decides it — the most recent speakers.
    // Null until the server says (an audio meeting, or an older server): then nothing is limited. The ref is
    // written synchronously by the hub handlers, so peer connections created in the same handler see it.
    const [videoSlots, setVideoSlotsState] = useState<string[] | null>(null);
    const videoSlotsRef = useRef<string[] | null>(null);
    const setVideoSlots = (active: string[]) => {
        videoSlotsRef.current = active;
        setVideoSlotsState(active);
    };
    // The connection id before the last rejoin: right after a reconnect the set can still name it.
    const prevJoinedConnRef = useRef<string | null>(null);
    // People in the room, you included — the camera's encoding follows it (cameraEncodingFor).
    const roomSizeRef = useRef(1);
    const [outgoing, setOutgoing] = useState<OutgoingPhase | null>(placingCall && isHost ? "ringing" : null);
    const [confirmMuteAll, setConfirmMuteAll] = useState(false);
    const everHadPeerRef = useRef(false);
    const declinedCountRef = useRef(0);
    const iceServersRef = useRef<RTCIceServer[]>(ICE_SERVERS);
    const [iceReady, setIceReady] = useState(false);
    // The SignalR handlers are memoised once; these let them read current values.
    const micOnRef = useRef(micOn);
    micOnRef.current = micOn;
    const cameraOnRef = useRef(cameraOn);
    cameraOnRef.current = cameraOn;
    const recorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunks = useRef<Blob[]>([]);
    const recordStartedAt = useRef<Date | null>(null);
    // Recording capture: the meeting tab's video, and an AudioContext mixing
    // the host's mic with every participant's audio track.
    const captureStreamRef = useRef<MediaStream | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
    const audioSourcesRef = useRef(new Map<string, MediaStreamAudioSourceNode>());
    const peersRef = useRef<Record<string, Peer>>({});
    peersRef.current = peers;
    const accessToken = useAccessToken();
    const tokenRef = useRef(accessToken);
    tokenRef.current = accessToken;
    const qc = useQueryClient();

    const peerConnections = useRef<Record<string, RTCPeerConnection>>({});
    const localStreamRef = useRef<MediaStream | null>(null);
    const joinedRef = useRef(false);
    // This room mount's id, sent on every (re)join so peers recognise us after our connection id changes.
    const [clientId] = useState(() => randomToken(12));
    const guestRef = useRef(guest);
    guestRef.current = guest;
    // Who the server says we are (a guest's id is a hash the browser can't compute itself).
    const selfUserIdRef = useRef<string | null>(null);
    const linksRef = useRef<Record<string, PeerLink>>({});
    const remoteStreamsRef = useRef<Record<string, Map<string, MediaStream>>>({});
    // connection id -> who is behind it, written synchronously as hub events arrive.
    const peerMetaRef = useRef<Record<string, { clientId: string; userId: string }>>({});
    const graceTimersRef = useRef<Record<string, number>>({});
    const pendingOfferTimersRef = useRef<Record<string, number>>({});
    // The connection id we last joined the room on; a different live id means "rejoin".
    const joinedConnRef = useRef<string | null>(null);
    const rejoinPendingRef = useRef(false);
    const [inRoom, setInRoom] = useState(false);
    const [everInRoom, setEverInRoom] = useState(false);
    // The peer-connection layer (defined below, refreshed every render) for the memoised hub handlers.
    const rtcRef = useRef<RtcHandlers | null>(null);

    const hub = useMeetingsHub(useMemo(() => ({
        [MeetingsEvents.Participants]: (payload) => {
            rtcRef.current?.onParticipants(payload as ParticipantsPayload);
        },
        [MeetingsEvents.UserJoined]: (payload) => {
            const isNew = rtcRef.current?.onUserJoined(payload as JoinedPayload) ?? true;
            // Audible ping when someone joins mid-call — not when someone is just reconnecting. Skipped on
            // first load because the initial participant snapshot fires via `Participants`.
            if (isNew) {
                try { playMessageTone(); } catch { /* autoplay blocked */ }
            }
        },
        [MeetingsEvents.UserLeft]: (payload) => {
            rtcRef.current?.onUserLeft(payload as { connectionId: string; reason?: string });
        },
        [MeetingsEvents.ReceiveOffer]: (payload) => {
            const p = payload as { fromConnectionId: string; offer: SignalDesc };
            void rtcRef.current?.onOffer(p.fromConnectionId, p.offer);
        },
        [MeetingsEvents.ReceiveAnswer]: (payload) => {
            const p = payload as { fromConnectionId: string; answer: SignalDesc };
            void rtcRef.current?.onAnswer(p.fromConnectionId, p.answer);
        },
        [MeetingsEvents.ReceiveIceCandidate]: (payload) => {
            const p = payload as { fromConnectionId: string; candidate: SignalIce };
            void rtcRef.current?.onCandidate(p.fromConnectionId, p.candidate);
        },
        [MeetingsEvents.MediaToggled]: (payload) => {
            const p = payload as { connectionId: string; mediaType: "audio" | "video"; enabled: boolean };
            setPeers((prev) => {
                const peer = prev[p.connectionId];
                if (!peer) return prev;
                return {
                    ...prev,
                    [p.connectionId]: {
                        ...peer,
                        audio: p.mediaType === "audio" ? p.enabled : peer.audio,
                        video: p.mediaType === "video" ? p.enabled : peer.video
                    }
                };
            });
        },
        [MeetingsEvents.HandRaised]: (payload) => {
            const p = payload as {
                userId: string;
                connectionId?: string;
                raised: boolean;
                raisedAt?: string | null;
                loweredByDisplayName?: string | null;
            };
            // My own hand: the server's time for the queue, or the host lowered it.
            if (p.connectionId && p.connectionId === hubRef.current?.connectionId) {
                setHandRaised(p.raised);
                setHandRaisedAt(p.raised ? p.raisedAt ?? null : null);
                if (!p.raised && p.loweredByDisplayName) setHandNotice(`${p.loweredByDisplayName} lowered your hand`);
                return;
            }
            setPeers((prev) => {
                const next = { ...prev };
                for (const id in next) {
                    // By connection when the server says which; older servers only sent the user.
                    if (p.connectionId ? id === p.connectionId : next[id]!.userId === p.userId) {
                        next[id] = { ...next[id]!, handRaised: p.raised, raisedAt: p.raised ? p.raisedAt ?? null : null };
                    }
                }
                return next;
            });
        },
        [MeetingsEvents.ScreenShare]: (payload) => {
            rtcRef.current?.onScreenShare(payload as { connectionId: string; sharing: boolean; streamId?: string | null });
        },
        [MeetingsEvents.Reaction]: (payload) => {
            const p = payload as { emoji: string; displayName?: string };
            // The clear used to compare against a fresh Date.now(), which never
            // matched, so a reaction stayed on screen until the next one.
            const key = Date.now() + Math.random();
            setReaction({ emoji: p.emoji, key, who: p.displayName });
            window.setTimeout(() => setReaction((r) => (r && r.key === key ? null : r)), 2500);
        },
        [MeetingsEvents.MeetingChat]: (payload) => {
            const p = payload as Partial<MeetingChatMessage> & { userId?: string; displayName?: string; message?: string };
            const author = p.authorUserId ?? p.userId ?? "";
            const mine = guest
                ? author === (selfUserIdRef.current ?? `guest:${hubRef.current?.connectionId ?? ""}`)
                : normalizeUserId(author) === myUserIdRef.current;
            if (guest) {
                const sentAt = p.sentAt ?? new Date().toISOString();
                const msg: MeetingChatMessage = {
                    id: p.id ?? `${author}-${sentAt}`,
                    meetingId,
                    authorUserId: author,
                    authorEmail: p.authorEmail ?? "",
                    authorDisplayName: p.authorDisplayName ?? p.displayName ?? null,
                    isGuest: Boolean(p.isGuest),
                    // Older servers sent plain text only.
                    bodyHtml: p.bodyHtml ?? `<p>${escapeText(p.message ?? "")}</p>`,
                    isEdited: false,
                    sentAt
                };
                setGuestChat((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
            } else {
                void qc.invalidateQueries({ queryKey: ["meeting-chat", meetingId] });
            }
            if (!mine && !chatOpenRef.current) setChatUnread((n) => n + 1);
        },
        [MeetingsEvents.MeetingChatChanged]: (payload) => {
            const p = payload as { messageId?: string; reason?: string; message?: MeetingChatMessage | null };
            if (guest) {
                setGuestChat((prev) => p.reason === "deleted"
                    ? prev.filter((m) => m.id !== p.messageId)
                    : p.message ? prev.map((m) => (m.id === p.messageId ? { ...m, ...p.message! } : m)) : prev);
                return;
            }
            void qc.invalidateQueries({
                queryKey: p.reason === "reaction" ? ["chat-reactions", "meetings", meetingId] : ["meeting-chat", meetingId]
            });
        },
        [MeetingsEvents.Caption]: (payload) => {
            const p = payload as { userId?: string; displayName?: string; text?: string; sentAt?: string };
            if (!p.text) return;
            const id = `${p.userId ?? "?"}-${p.sentAt ?? Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setIncomingCaptions((prev) => [
                ...prev.slice(-7),
                { id, speaker: p.displayName ?? "Someone", text: p.text! }
            ]);
        },
        [MeetingsEvents.MeetingEnded]: (payload) => {
            const p = payload as { reason?: string };
            if (p.reason === "last-participant") {
                // Being the only one left is NOT a reason to hang up on
                // someone. You are usually alone because you joined first,
                // or because the other side is reconnecting — kicking you
                // out here meant the meeting closed under you and the link
                // was already marked Ended, so nobody could get back in.
                // Say so, and let the user decide when to leave.
                setAutoEndMsg("You're the only one here. Waiting for others to join…");
                window.setTimeout(() => setAutoEndMsg(null), 6000);
            } else {
                onLeave();
            }
        },
        [MeetingsEvents.ForceMuted]: (payload) => {
            // Two flavours of force-mute:
            //   - { meetingId, byUserId, byDisplayName }                    → mute-all (everyone except sender)
            //   - { meetingId, targetConnectionId, byUserId, byDisplayName } → mute one specific connection
            const p = payload as { targetConnectionId?: string };
            const targeted = typeof p.targetConnectionId === "string" && p.targetConnectionId.length > 0;
            // If the broadcast names a target, only honour it when it's me.
            // The myConnectionId snapshot lives on the hub object — the
            // hub.connectionId getter is updated as we connect.
            if (targeted && p.targetConnectionId !== hubRef.current?.connectionId) return;
            setMicOn(false);
            micOnRef.current = false;
            if (localStreamRef.current) {
                for (const t of localStreamRef.current.getAudioTracks()) t.enabled = false;
            }
            // Re-announce, or everyone else's icon for us stays "unmuted".
            void hubRef.current?.toggleMedia("audio", false);
        },
        [MeetingsEvents.SpotlightChanged]: (payload) => {
            const p = payload as { spotlightConnectionId?: string };
            const id = p.spotlightConnectionId ?? "";
            setSpotlightConnectionId(id.length > 0 ? id : null);
            // Functional update — this handler is memoised once, so it read a stale layoutMode.
            if (id.length > 0) setLayoutMode((m) => (m === "gallery" ? "spotlight" : m));
        },
        [MeetingsEvents.LayoutSynced]: (payload) => {
            const p = payload as { layoutMode?: string };
            if (p.layoutMode === "gallery" || p.layoutMode === "spotlight" || p.layoutMode === "focus" || p.layoutMode === "together") {
                setLayoutMode(p.layoutMode);
            }
        },
        [MeetingsEvents.RecordingState]: (payload) => {
            const p = payload as { byConnectionId?: string; byDisplayName?: string; isRecording?: boolean; replay?: boolean };
            if (p.isRecording) {
                const who = p.byDisplayName || "The organizer";
                const mine = Boolean(p.byConnectionId) && p.byConnectionId === hubRef.current?.connectionId;
                setRoomRecording({ byConnectionId: p.byConnectionId ?? "", byDisplayName: who });
                setRecordingNotice(mine
                    ? "You started recording. It will be saved to your Drive and shared with attendees."
                    : p.replay
                        ? `This meeting is being recorded by ${who}. The recording will be saved to their Drive and shared with attendees.`
                        : `${who} started recording. The recording will be saved to their Drive and shared with attendees.`);
            } else {
                setRoomRecording(null);
                setRecordingNotice(null);
            }
        },
        [MeetingsEvents.RecordingSaved]: (_payload) => {
            void qc.invalidateQueries({ queryKey: ["meeting-recordings", meetingId] });
            setRecordingNotice("The recording has been saved — it's in the meeting chat.");
        },
        // ── Lobby events ─────────────────────────────────────────────────────
        [MeetingsEvents.LobbyKnock]: (payload) => {
            // A new participant is waiting in the lobby. Add to the host's
            // admit/deny list. Non-host participants see it too so they
            // can see someone is waiting (Teams shows "X wants to join").
            const p = payload as LobbyKnocker;
            setLobbyKnockers((prev) => {
                if (prev.some((k) => k.connectionId === p.connectionId)) return prev;
                return [...prev, p];
            });
        },
        [MeetingsEvents.LobbyAdmitted]: (_payload) => {
            // Admitted — join for real. A guest has to re-join as a guest; this
            // always called JoinMeeting, which a guest has no identity for.
            setLobbyState("idle");
            doJoinRef.current();
        },
        [MeetingsEvents.MeetingLocked]: (payload) => {
            const p = payload as { locked?: boolean; blocked?: boolean; byDisplayName?: string; reason?: string };
            if (p.reason === "full") {
                setMeetingFull(true);
                setLockedOut(true);
                setInRoom(false);
                return;
            }
            setMeetingFull(false);
            setMeetingLocked(Boolean(p.locked));
            if (p.blocked) {
                setLockedOut(true);
                setInRoom(false);
                return;
            }
            setLockedOut(false);
            if (p.byDisplayName) {
                setHandNotice(`${p.byDisplayName} ${p.locked ? "locked" : "unlocked"} the meeting${p.locked ? ". Nobody new can join." : "."}`);
                window.setTimeout(() => setHandNotice(null), 5000);
            }
        },
        [MeetingsEvents.LobbyDenied]: (_payload) => {
            setLobbyState("denied");
        },
        [MeetingsEvents.LobbyWaiting]: (_payload) => {
            setLobbyState("waiting");
            rejoinPendingRef.current = false;
            setInRoom(true);
        },
        [MeetingsEvents.LobbyLeft]: (payload) => {
            const p = payload as { connectionId: string };
            setLobbyKnockers((prev) => prev.filter((k) => k.connectionId !== p.connectionId));
        },
        [MeetingsEvents.VideoSlots]: (payload) => {
            const p = payload as { active?: string[] };
            if (Array.isArray(p.active)) setVideoSlots(p.active);
        },
        [MeetingsEvents.CallDeclined]: (_payload) => {
            // One decline ends a 1:1 call; a group call ends once everyone rung has declined.
            declinedCountRef.current += 1;
            if (everHadPeerRef.current) return;
            if (calleeCount <= 1 || declinedCountRef.current >= calleeCount) {
                setOutgoing("declined");
                void connectParityApi.endMeeting(meetingId, tokenRef.current).catch(() => undefined);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [onLeave]), { guest: isGuest });
    // The handler map above is memoised once and would otherwise hold the
    // first render's hub (stale connectionId).
    const hubRef = useRef<UseMeetingsHub | null>(null);
    hubRef.current = hub;

    // Someone else opened the whiteboard (first one on it): offer to join them.
    const hubOn = hub.on;
    useEffect(() => hubOn(MeetingsEvents.WhiteboardState, (payload) => {
        const p = payload as { reason?: string; byDisplayName?: string | null; byConnectionId?: string | null; viewers?: number };
        if (p.reason === "opened" && p.viewers === 1 && p.byConnectionId && p.byConnectionId !== hubRef.current?.connectionId) {
            setWbInvite(p.byDisplayName || "Someone");
        }
    }), [hubOn]);
    useEffect(() => {
        if (!wbInvite) return;
        const t = window.setTimeout(() => setWbInvite(null), 12_000);
        return () => window.clearTimeout(t);
    }, [wbInvite]);

    // One side panel at a time: chat, people or invite closes the notes.
    useEffect(() => {
        if (chatOpen || rosterOpen || inviteOpen) setNotesOpen(false);
    }, [chatOpen, rosterOpen, inviteOpen]);

    const announceMedia = useCallback(() => {
        const h = hubRef.current;
        if (!h) return;
        void h.toggleMedia("audio", micOnRef.current);
        if (!isAudio) void h.toggleMedia("video", cameraOnRef.current);
    }, [isAudio]);

    // --- Local media + join ---------------------------------------------

    useEffect(() => {
        let cancelled = false;
        const acquire = async () => {
            try {
                // Apply browser-native echo cancellation, noise suppression, and
                // auto gain control at the hardware/OS layer. This is the first
                // line of defence; the RNNoise worklet adds a second software layer
                // on top when the user enables the noise suppressor toggle.
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                        // Honour the microphone chosen in the green room.
                        ...(deviceIds?.micId ? { deviceId: { exact: deviceIds.micId } } : {})
                    },
                    video: isAudio ? false : {
                        // Suggest a balanced resolution; the browser picks the
                        // closest supported mode. 720p at 30 fps is the Teams
                        // default for HD calls.
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30 },
                        ...(deviceIds?.cameraId ? { deviceId: { exact: deviceIds.cameraId } } : {})
                    }
                });
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                // Honour the initial mic/cam state (from the guest green room, or
                // an audio-only call) rather than leaving every track hot.
                stream.getAudioTracks().forEach((t) => { t.enabled = micOn; });
                stream.getVideoTracks().forEach((t) => { t.enabled = cameraOn; });
                setLocalStream(stream);
                localStreamRef.current = stream;
            } catch (err) {
                if (cancelled) return;
                // A camera released microseconds earlier (by the green-room
                // preview, or another tab) often still reports NotReadableError
                // / "Device in use" on Windows. One retry after a short pause
                // clears it; without this the user joined deaf and blind.
                const name = err instanceof DOMException ? err.name : "";
                if (name === "NotReadableError" || name === "AbortError") {
                    await new Promise((r) => setTimeout(r, 600));
                    if (cancelled) return;
                    try {
                        const retry = await navigator.mediaDevices.getUserMedia({
                            audio: true,
                            video: !isAudio
                        });
                        if (cancelled) { retry.getTracks().forEach((t) => t.stop()); return; }
                        retry.getAudioTracks().forEach((t) => { t.enabled = micOn; });
                        retry.getVideoTracks().forEach((t) => { t.enabled = cameraOn; });
                        setLocalStream(retry);
                        localStreamRef.current = retry;
                        return;
                    } catch { /* fall through to the error below */ }
                }
                setMediaError(err instanceof Error ? err.message : "Camera / mic blocked.");
            } finally {
                // Settled either way. Joining used to be gated on a successful
                // stream, so anyone who denied permission — or whose camera was
                // still held by another app or by the green-room preview —
                // silently never joined the room at all: they saw no peers and
                // no peer saw them. Teams lets you in without devices; so do we.
                if (!cancelled) setMediaSettled(true);
            }
        };
        void acquire();
        return () => {
            cancelled = true;
        };
    }, [isAudio, deviceIds?.cameraId, deviceIds?.micId]);

    // STUN + short-lived TURN from the server. The relay list used to be baked
    // into the bundle at build time and was never set in production, so anyone
    // behind a corporate firewall got a black tile.
    useEffect(() => {
        let cancelled = false;
        const settle = () => { if (!cancelled) setIceReady(true); };
        const fallback = window.setTimeout(settle, 3000);
        void connectParityApi.iceServers(meetingId)
            .then((servers) => {
                if (cancelled) return;
                const parsed: RTCIceServer[] = servers
                    .filter((s) => Array.isArray(s.urls) && s.urls.length > 0)
                    .map((s) => (s.username && s.credential
                        ? { urls: s.urls, username: s.username, credential: s.credential }
                        : { urls: s.urls }));
                if (parsed.length > 0) {
                    iceServersRef.current = [...parsed, ...ICE_SERVERS.filter((x) => x.username)];
                }
            })
            .catch(() => undefined)
            .finally(() => { window.clearTimeout(fallback); settle(); });
        return () => { cancelled = true; window.clearTimeout(fallback); };
    }, [meetingId]);

    // Join once the hub is connected and media + ICE servers are ready — and REJOIN whenever the live
    // connection id changes. Automatic reconnect after a network drop or a server restart hands us a NEW
    // connection id, and the server only knows the old one left. This used to be a one-shot guarded by
    // joinedRef: after any reconnect you were silently out of the room — no signalling, and no join or
    // lobby events (the host never saw anyone rejoin or knock) until you left the call and came back.
    // The rejoin carries the same client id (and a guest's same token), so peers keep their media up and
    // an admitted guest isn't sent back to the lobby. The server decides about the lobby (LobbyWaiting).
    const doJoin = useCallback(() => {
        const h = hubRef.current;
        if (!h) return;
        const g = guestRef.current;
        if (g) {
            void (g.token
                ? h.joinMeetingAsGuestWithSession(meetingId, g.displayName, g.token, clientId)
                : h.joinMeetingAsGuest(meetingId, g.displayName));
        } else {
            void h.joinMeetingWithSession(meetingId, clientId);
        }
    }, [meetingId, clientId]);
    const doJoinRef = useRef(doJoin);
    doJoinRef.current = doJoin;

    useEffect(() => {
        if (hub.state !== HubConnectionState.Connected || !hub.connectionId) return;
        if (!mediaSettled || !iceReady) return;
        if (joinedConnRef.current === hub.connectionId) return;
        rejoinPendingRef.current = joinedConnRef.current !== null;
        prevJoinedConnRef.current = joinedConnRef.current;
        joinedConnRef.current = hub.connectionId;
        joinedRef.current = true;
        if (rejoinPendingRef.current) {
            // Whoever can admit gets the waiting list replayed on join; our rows carry dead connection ids.
            setLobbyKnockers([]);
            setInRoom(false);
        }
        doJoin();
    }, [hub.state, hub.connectionId, mediaSettled, iceReady, doJoin]);

    // In a call: the sign-in guard must not navigate the page away (it waits until the call ends).
    useEffect(() => enterCall(), []);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            // Closing the room mid-recording (the meeting ended, or you
            // navigated away) still saves it — the saver outlives the room.
            if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
            // Leave when the shared connection is finally released — unless the room is mounted again
            // within its grace period, in which case the new room carries on on the same connection.
            if (joinedRef.current) hubRef.current?.leaveMeetingOnRelease(meetingId);
            for (const t of Object.values(graceTimersRef.current)) window.clearTimeout(t);
            for (const t of Object.values(pendingOfferTimersRef.current)) window.clearTimeout(t);
            for (const link of Object.values(linksRef.current)) {
                link.closed = true;
                if (link.disconnectTimer !== null) window.clearTimeout(link.disconnectTimer);
                link.pc.close();
            }
            linksRef.current = {};
            peerConnections.current = {};
            localStreamRef.current?.getTracks().forEach((t) => t.stop());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Peer connections (perfect negotiation) ---------------------------
    //
    // One RTCPeerConnection per remote participant, negotiated with the W3C "perfect negotiation" pattern:
    // either side may (re)offer at any time — adding a screen share, an ICE restart after a network change,
    // a rebuilt connection — and a collision is settled by politeness (the polite side rolls back). Every
    // offer/answer/candidate carries the sender's and the target's connection "session", so a peer that
    // rebuilt its RTCPeerConnection (reload, remount) is recognised and ours is rebuilt to match, and
    // candidates for an old connection are dropped instead of breaking the new one.
    //
    // A link is keyed by the remote's CURRENT hub connection id but belongs to their client id: when they
    // reconnect to the hub under a new id we re-key it and the media keeps flowing — it is peer-to-peer and
    // never needed the server once connected. Only a link that actually failed is renegotiated.
    //
    // Everything below only touches refs and state setters, so callbacks captured by timers or by a peer
    // connection's handlers stay correct across renders.

    const syncPcMap = () => {
        peerConnections.current = Object.fromEntries(Object.entries(linksRef.current).map(([id, l]) => [id, l.pc]));
    };

    const upsertPeer = (connectionId: string, p: { userId: string; email: string; displayName: string; clientId: string }) => {
        setPeers((prev) => {
            const existing = prev[connectionId];
            if (existing) {
                if (existing.clientId === p.clientId && !existing.reconnecting) return prev;
                return { ...prev, [connectionId]: { ...existing, clientId: p.clientId, reconnecting: false } };
            }
            return {
                ...prev,
                [connectionId]: {
                    connectionId,
                    clientId: p.clientId,
                    userId: p.userId,
                    email: p.email,
                    displayName: p.displayName,
                    stream: null,
                    screenStream: null,
                    screenStreamId: null,
                    audio: true,
                    video: true,
                    handRaised: false,
                    sharing: false,
                    reconnecting: false
                }
            };
        });
    };

    /** Recompute a peer's camera and screen streams from what has arrived on their connection. */
    const refreshPeerStreams = (id: string) => {
        const map = remoteStreamsRef.current[id];
        setPeers((prev) => {
            const peer = prev[id];
            if (!peer) return prev;
            const live = map ? [...map.values()].filter((s) => s.getTracks().some((t) => t.readyState === "live")) : [];
            const screenId = peer.screenStreamId;
            const main = live.find((s) => s.id !== screenId && s.getAudioTracks().length > 0)
                ?? live.find((s) => s.id !== screenId)
                ?? null;
            const screen = screenId
                ? live.find((s) => s.id === screenId) ?? null
                // Announced without a stream id (older server): the extra stream is the screen.
                : peer.sharing && live.length > 1 ? live.find((s) => s !== main) ?? null : null;
            if (main === peer.stream && screen === peer.screenStream) return prev;
            return { ...prev, [id]: { ...peer, stream: main, screenStream: screen } };
        });
    };

    const closeLink = (id: string) => {
        const link = linksRef.current[id];
        if (!link) return;
        link.closed = true;
        if (link.disconnectTimer !== null) window.clearTimeout(link.disconnectTimer);
        link.pc.close();
        delete linksRef.current[id];
        delete remoteStreamsRef.current[id];
        syncPcMap();
        setPeers((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id]!, stream: null, screenStream: null } } : prev));
    };

    const clearTimers = (id: string) => {
        const grace = graceTimersRef.current[id];
        if (grace !== undefined) {
            window.clearTimeout(grace);
            delete graceTimersRef.current[id];
        }
        const offer = pendingOfferTimersRef.current[id];
        if (offer !== undefined) {
            window.clearTimeout(offer);
            delete pendingOfferTimersRef.current[id];
        }
    };

    const teardownPeer = (id: string) => {
        closeLink(id);
        clearTimers(id);
        delete peerMetaRef.current[id];
        setPeers((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    /** Their connection dropped: keep the tile and the peer connection a while — they may come back. */
    const markReconnecting = (id: string) => {
        if (graceTimersRef.current[id] !== undefined) return;
        setPeers((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id]!, reconnecting: true } } : prev));
        graceTimersRef.current[id] = window.setTimeout(() => {
            delete graceTimersRef.current[id];
            teardownPeer(id);
        }, PEER_GRACE_MS);
    };

    /** The same person (client id) is back under a new connection id: move everything over. */
    const rekeyPeer = (oldId: string, newId: string) => {
        if (oldId === newId) return;
        const link = linksRef.current[oldId];
        if (linksRef.current[newId]) {
            // They already reached us on the new id with a fresh connection; the old one is obsolete.
            closeLink(oldId);
        } else if (link) {
            delete linksRef.current[oldId];
            link.remoteId = newId;
            linksRef.current[newId] = link;
            syncPcMap();
        }
        const streams = remoteStreamsRef.current[oldId];
        if (streams && !remoteStreamsRef.current[newId]) remoteStreamsRef.current[newId] = streams;
        delete remoteStreamsRef.current[oldId];
        const meta = peerMetaRef.current[oldId];
        if (meta) peerMetaRef.current[newId] = meta;
        delete peerMetaRef.current[oldId];
        clearTimers(oldId);
        setPeers((prev) => {
            const old = prev[oldId];
            if (!old) return prev;
            const next = { ...prev };
            delete next[oldId];
            next[newId] = prev[newId]
                ? { ...prev[newId]!, reconnecting: false }
                : { ...old, connectionId: newId, reconnecting: false };
            return next;
        });
        setSpotlightConnectionId((s) => (s === oldId ? newId : s));
    };

    const sendDescription = (link: PeerLink) => {
        const d = link.pc.localDescription;
        const h = hubRef.current;
        if (!d || !h || link.closed) return;
        const payload = { type: d.type, sdp: d.sdp, s: link.localSession, t: link.remoteSession };
        void (d.type === "offer" ? h.sendOffer(link.remoteId, payload) : h.sendAnswer(link.remoteId, payload));
    };

    const flushCandidates = async (link: PeerLink) => {
        for (const c of link.pending.splice(0)) {
            try { await link.pc.addIceCandidate(c); } catch { /* for an older ICE generation */ }
        }
    };

    /** New ICE candidates on the same connection (network change, NAT rebinding); negotiationneeded re-offers. */
    const restartIce = (link: PeerLink) => {
        if (link.closed || link.pc.signalingState === "closed") return;
        try { link.pc.restartIce(); } catch { /* very old browser */ }
    };

    /**
     * Whether we may send our camera now (FPT-7): always in an audio meeting's sense (there is no camera), and
     * otherwise only while the hub has us in the video set. Read from refs, so it is current inside handlers.
     */
    const canSendVideo = (): boolean =>
        isAudio || maySendVideo(videoSlotsRef.current, [hubRef.current?.connectionId, joinedConnRef.current, prevJoinedConnRef.current]);

    /** The sender caps for one connection, for the room's current size and whether we present. */
    const applyCaps = (link: PeerLink) => {
        if (link.closed) return;
        applySenderCaps(link.pc, { camera: link.cameraSender, screen: link.screenSender },
            cameraEncodingFor(roomSizeRef.current, sharingRef.current));
    };

    /**
     * Our microphone, camera and (while presenting) screen, each on its own sender. Every new or rebuilt
     * connection gets the CURRENT tracks — including a noise-suppressed or blurred replacement — so a
     * rejoin or renegotiation can never come up without our audio. The screen is a separate, second video
     * track, so the camera keeps going while we present.
     */
    const attachLocalMedia = (link: PeerLink, asOfferer: boolean) => {
        const { pc } = link;
        const ls = localStreamRef.current;
        const mic = ls?.getAudioTracks()[0];
        // Nothing to send? An offer still needs media sections, or we'd be blind and deaf; an answerer
        // gets them from the offer.
        if (mic && ls) link.audioSender = pc.addTrack(mic, ls);
        else if (asOfferer) pc.addTransceiver("audio", { direction: "recvonly" });
        if (isAudio) return;
        const cam = ls?.getVideoTracks()[0];
        if (cam && ls) {
            link.cameraSender = pc.addTrack(cam, ls);
            // Outside the video set: negotiated (so rotating in needs no renegotiation) but sending nothing.
            if (!canSendVideo()) void link.cameraSender.replaceTrack(null).catch(() => undefined);
        }
        else if (asOfferer) pc.addTransceiver("video", { direction: "recvonly" });
        const share = shareStreamRef.current;
        const screen = share?.getVideoTracks()[0];
        if (share && screen) link.screenSender = pc.addTrack(screen, share);
    };

    const createLink = (remoteId: string, asOfferer: boolean): PeerLink => {
        const existing = linksRef.current[remoteId];
        if (existing) return existing;
        const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
        const remoteClient = peerMetaRef.current[remoteId]?.clientId ?? remoteId;
        const link: PeerLink = {
            pc,
            remoteId,
            localSession: randomToken(9),
            remoteSession: null,
            // Both sides must agree and it must survive reconnects: compare the stable client ids.
            polite: clientId < remoteClient,
            makingOffer: false,
            ignoreOffer: false,
            pending: [],
            audioSender: null,
            cameraSender: null,
            screenSender: null,
            disconnectTimer: null,
            closed: false,
            fallbackStream: null
        };
        linksRef.current[remoteId] = link;
        syncPcMap();
        attachLocalMedia(link, asOfferer);
        preferCodecs(pc);

        pc.ontrack = (ev) => {
            let stream = ev.streams[0];
            if (!stream) {
                link.fallbackStream ??= new MediaStream();
                link.fallbackStream.addTrack(ev.track);
                stream = link.fallbackStream;
            }
            const id = link.remoteId;
            const map = remoteStreamsRef.current[id] ?? new Map<string, MediaStream>();
            remoteStreamsRef.current[id] = map;
            map.set(stream.id, stream);
            const refresh = () => refreshPeerStreams(link.remoteId);
            stream.addEventListener("removetrack", refresh);
            stream.addEventListener("addtrack", refresh);
            ev.track.addEventListener("ended", refresh);
            ev.track.addEventListener("unmute", refresh);
            refresh();
        };

        pc.onicecandidate = (ev) => {
            if (!ev.candidate || link.closed) return;
            void hubRef.current?.sendIceCandidate(link.remoteId, { ...ev.candidate.toJSON(), s: link.localSession, t: link.remoteSession });
        };

        pc.onnegotiationneeded = async () => {
            if (link.closed) return;
            try {
                link.makingOffer = true;
                preferCodecs(pc);
                await pc.setLocalDescription();
                sendDescription(link);
            } catch {
                // Rolled back by a colliding offer, or closed — the other path takes over.
            } finally {
                link.makingOffer = false;
            }
        };

        pc.onsignalingstatechange = () => {
            if (pc.signalingState === "stable") applyCaps(link);
        };

        // Recover the media path: `failed` → ICE restart now; `disconnected` for more than 5 s → ICE restart.
        const onStateChange = () => {
            if (link.closed) return;
            const s = pc.connectionState;
            const ice = pc.iceConnectionState;
            if (s === "connected" || ice === "connected" || ice === "completed") {
                if (link.disconnectTimer !== null) {
                    window.clearTimeout(link.disconnectTimer);
                    link.disconnectTimer = null;
                }
                return;
            }
            if (s === "failed" || ice === "failed") {
                restartIce(link);
                return;
            }
            if ((s === "disconnected" || ice === "disconnected") && link.disconnectTimer === null) {
                link.disconnectTimer = window.setTimeout(() => {
                    link.disconnectTimer = null;
                    if (pc.connectionState === "disconnected" || pc.iceConnectionState === "disconnected") restartIce(link);
                }, 5000);
            }
        };
        pc.onconnectionstatechange = onStateChange;
        pc.oniceconnectionstatechange = onStateChange;
        return link;
    };

    const onParticipants = (p: ParticipantsPayload) => {
        if (p.self?.userId) selfUserIdRef.current = p.self.userId;
        if (typeof p.maxVideoParticipants === "number") setMaxVideo(p.maxVideoParticipants);
        // Before any peer connection is made below, so new connections start gated correctly.
        const slotsKnown = Array.isArray(p.videoSlots);
        if (slotsKnown) setVideoSlots(p.videoSlots!);
        // An older server: past the cap you joined with the camera off and stayed that way.
        if (p.videoLimited && !slotsKnown) {
            setVideoLimited(true);
            setCameraOn(false);
            cameraOnRef.current = false;
            localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = false; });
        }
        if (p.peers.length > 0) {
            everHadPeerRef.current = true;
            setOutgoing(null);
        }
        const listed = new Set<string>();
        for (const peer of p.peers) {
            listed.add(peer.connectionId);
            const cid = peer.clientId || peer.connectionId;
            // Someone we're already connected to, under a new connection id (after a server restart).
            if (!peerMetaRef.current[peer.connectionId]) {
                const previous = Object.entries(peerMetaRef.current)
                    .find(([id, m]) => id !== peer.connectionId && m.clientId === cid)?.[0];
                if (previous) rekeyPeer(previous, peer.connectionId);
            }
            peerMetaRef.current[peer.connectionId] = { clientId: cid, userId: peer.userId };
            upsertPeer(peer.connectionId, { ...peer, clientId: cid });
            const link = linksRef.current[peer.connectionId];
            if (!link || link.pc.connectionState === "failed" || link.pc.connectionState === "closed") {
                if (link) closeLink(peer.connectionId);
                // We're the newcomer, so we offer (negotiationneeded fires once our tracks are added).
                createLink(peer.connectionId, true);
            } else if (link.pc.connectionState !== "connected") {
                // Kept across our reconnect but not healthy: resend an offer that was lost while the hub was
                // down, or restart ICE.
                if (link.pc.signalingState === "have-local-offer") sendDescription(link);
                else restartIce(link);
            }
        }
        if (rejoinPendingRef.current) {
            // Anyone we had who isn't in the room (yet) may be reconnecting too (a server restart drops
            // everyone at once) — keep them for the grace period.
            for (const id of Object.keys(peerMetaRef.current)) if (!listed.has(id)) markReconnecting(id);
            // What the room forgot along with our old connection.
            if (sharingRef.current) void hubRef.current?.screenShareStream(true, shareStreamRef.current?.id ?? null);
            if (handRaisedRef.current) void hubRef.current?.raiseHand(true);
            if (isHost && recorderRef.current && recorderRef.current.state !== "inactive") void hubRef.current?.setRecordingState(true);
        }
        rejoinPendingRef.current = false;
        setInRoom(true);
        setEverInRoom(true);
        // Tell the room our real mic/camera state. Peers assumed everyone
        // joined unmuted with the camera on, so someone who joined muted
        // looked unmuted until they toggled.
        announceMedia();
    };

    const onUserJoined = (p: JoinedPayload): boolean => {
        everHadPeerRef.current = true;
        setOutgoing(null);
        const cid = p.clientId || p.connectionId;
        let returning = false;
        const replaced = p.replacesConnectionId;
        if (replaced && replaced !== p.connectionId && (peerMetaRef.current[replaced] || linksRef.current[replaced])) {
            rekeyPeer(replaced, p.connectionId);
            returning = true;
        } else {
            const previous = Object.entries(peerMetaRef.current)
                .find(([id, m]) => id !== p.connectionId && m.clientId === cid)?.[0];
            if (previous) {
                rekeyPeer(previous, p.connectionId);
                returning = true;
            }
        }
        // Same connection, new browser session (their room remounted): their old peer connection is gone.
        const known = peerMetaRef.current[p.connectionId];
        if (known && known.clientId !== cid) closeLink(p.connectionId);
        if (!returning) {
            // The same person back after a reload (new session): drop the stale tile we kept for them.
            for (const [id, m] of Object.entries(peerMetaRef.current)) {
                if (id !== p.connectionId && m.userId === p.userId && m.clientId !== cid && graceTimersRef.current[id] !== undefined) {
                    teardownPeer(id);
                }
            }
        }
        peerMetaRef.current[p.connectionId] = { clientId: cid, userId: p.userId };
        upsertPeer(p.connectionId, { ...p, clientId: cid });
        // Re-announce an in-progress screen share to whoever just joined. ScreenShare is only broadcast at
        // toggle time, so without this a late joiner never learns anyone is presenting.
        if (sharingRef.current) void hubRef.current?.screenShareStream(true, shareStreamRef.current?.id ?? null);
        announceMedia();
        // The newcomer offers. If nothing arrives (an older client, a message lost in a reconnect), we do.
        const pending = pendingOfferTimersRef.current[p.connectionId];
        if (pending !== undefined) window.clearTimeout(pending);
        pendingOfferTimersRef.current[p.connectionId] = window.setTimeout(() => {
            delete pendingOfferTimersRef.current[p.connectionId];
            const link = linksRef.current[p.connectionId];
            if (!link && peerMetaRef.current[p.connectionId]) createLink(p.connectionId, true);
            else if (link && link.pc.connectionState !== "connected" && link.pc.signalingState === "stable") restartIce(link);
        }, 8000);
        return !returning;
    };

    const onUserLeft = (p: { connectionId: string; reason?: string }) => {
        // A dropped connection may come back (network blip, server restart); pressing Leave doesn't.
        if (p.reason === "disconnected") markReconnecting(p.connectionId);
        else teardownPeer(p.connectionId);
    };

    const onOffer = async (from: string, raw: SignalDesc) => {
        if (!raw?.sdp) return;
        let link = linksRef.current[from];
        // Their side rebuilt its peer connection: rebuild ours to match (DTLS/ICE can't carry over).
        if (link && raw.s && link.remoteSession && raw.s !== link.remoteSession) {
            closeLink(from);
            link = undefined;
        }
        if (!link) link = createLink(from, false);
        if (raw.s) link.remoteSession = raw.s;
        const pc = link.pc;
        const collision = link.makingOffer || pc.signalingState !== "stable";
        link.ignoreOffer = !link.polite && collision;
        if (link.ignoreOffer) return;
        const pending = pendingOfferTimersRef.current[from];
        if (pending !== undefined) {
            window.clearTimeout(pending);
            delete pendingOfferTimersRef.current[from];
        }
        try {
            // usedtx=1: our Opus encoder sends (almost) nothing while we are silent — see withOpusDtx.
            await pc.setRemoteDescription({ type: "offer", sdp: withOpusDtx(raw.sdp) });
            preferCodecs(pc);
            await flushCandidates(link);
            await pc.setLocalDescription();
            sendDescription(link);
        } catch (err) {
            console.warn("Couldn't answer a call offer", err);
        }
    };

    const onAnswer = async (from: string, raw: SignalDesc) => {
        const link = linksRef.current[from];
        if (!link || !raw?.sdp) return;
        // An answer to an offer from a peer connection we have since replaced.
        if (raw.t && raw.t !== link.localSession) return;
        if (link.pc.signalingState !== "have-local-offer") return;
        if (raw.s) link.remoteSession = raw.s;
        try {
            await link.pc.setRemoteDescription({ type: "answer", sdp: withOpusDtx(raw.sdp) });
            await flushCandidates(link);
        } catch (err) {
            console.warn("Couldn't apply a call answer", err);
        }
    };

    const onCandidate = async (from: string, raw: SignalIce) => {
        const link = linksRef.current[from];
        if (!link || !raw) return;
        if (raw.t && raw.t !== link.localSession) return;
        if (raw.s && link.remoteSession && raw.s !== link.remoteSession) return;
        const cand: RTCIceCandidateInit = {
            candidate: raw.candidate,
            sdpMid: raw.sdpMid,
            sdpMLineIndex: raw.sdpMLineIndex,
            usernameFragment: raw.usernameFragment
        };
        if (!link.pc.remoteDescription) {
            link.pending.push(cand);
            return;
        }
        try { await link.pc.addIceCandidate(cand); } catch { /* an older ICE generation, or an ignored offer's */ }
    };

    const onScreenShare = (p: { connectionId: string; sharing: boolean; streamId?: string | null }) => {
        setPeers((prev) => {
            const peer = prev[p.connectionId];
            if (!peer) return prev;
            return { ...prev, [p.connectionId]: { ...peer, sharing: p.sharing, screenStreamId: p.sharing ? (p.streamId ?? null) : null } };
        });
        refreshPeerStreams(p.connectionId);
    };

    rtcRef.current = { onParticipants, onUserJoined, onUserLeft, onOffer, onAnswer, onCandidate, onScreenShare };

    // --- Invite participants --------------------------------------------

    useEffect(() => {
        if (inviteQuery.trim().length < 2) { setInviteSuggestions([]); return; }
        const timer = window.setTimeout(async () => {
            try {
                const results = await connectParityApi.searchDirectory(inviteQuery.trim(), accessToken);
                setInviteSuggestions(results);
            } catch { setInviteSuggestions([]); }
        }, 300);
        return () => window.clearTimeout(timer);
    }, [inviteQuery, accessToken]);

    const inviteParticipant = async (email: string) => {
        setInviteStatus("sending");
        try {
            await connectParityApi.inviteToMeeting(meetingId, [email], accessToken);
            setInviteStatus("sent");
            setInviteQuery("");
            setInviteSuggestions([]);
            window.setTimeout(() => setInviteStatus("idle"), 3000);
        } catch {
            setInviteStatus("error");
            window.setTimeout(() => setInviteStatus("idle"), 3000);
        }
    };

    // --- UI actions -----------------------------------------------------

    const toggleMic = () => {
        const next = !micOn;
        setMicOn(next);
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = next; });
        }
        void hub.toggleMedia("audio", next);
    };

    const toggleCamera = () => {
        const next = !cameraOn;
        // Past the mesh cap, newcomers stay audio-only until someone leaves.
        if (next && videoLimited) return;
        setCameraOn(next);
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = next; });
        }
        void hub.toggleMedia("video", next);
    };

    const toggleHand = () => {
        const next = !handRaised;
        setHandRaised(next);
        if (!next) setHandRaisedAt(null);
        void hub.raiseHand(next);
    };

    /** The People pane's "Lower": your own hand, or (host) someone else's. */
    const lowerHandOf = (connectionId: string) => {
        if (connectionId === "self") {
            if (handRaised) toggleHand();
            return;
        }
        void hub.lowerHand(connectionId);
    };

    /**
     * Toggle background blur. When enabled, pipes the local camera
     * track through MediaPipe Selfie Segmentation and re-publishes
     * the canvas-captured track to every peer connection. When
     * disabled, swaps back to the original camera track and tears
     * down the segmenter.
     *
     * The original cam track stays referenced in
     * <c>originalCamTrackRef</c> so we never touch the underlying
     * webcam — if we did, the user's OS privacy indicator would
     * flicker on every toggle.
     */
    const toggleBlur = async () => {
        if (blurBusy) return;
        const cam = localStreamRef.current?.getVideoTracks()[0];
        if (!cam) return;

        setBlurBusy(true);
        try {
            if (!blurOn) {
                // Stash the unfiltered track exactly once. Subsequent
                // re-toggles reuse it.
                if (!originalCamTrackRef.current) {
                    originalCamTrackRef.current = cam;
                }
                const filter = new BackgroundFilter(originalCamTrackRef.current);
                const out = await filter.start();
                blurFilterRef.current = filter;
                replaceLocalVideoTrack(out);
                setBlurOn(true);
            } else {
                blurFilterRef.current?.stop();
                blurFilterRef.current = null;
                if (originalCamTrackRef.current) {
                    replaceLocalVideoTrack(originalCamTrackRef.current);
                }
                setBlurOn(false);
            }
        } catch (err) {
            // Surface a quiet error in the existing media-error slot
            // rather than throwing into the toolbar. Reset state so
            // the next click retries cleanly.
            setMediaError(err instanceof Error ? err.message : "Couldn't enable background blur.");
            blurFilterRef.current?.stop();
            blurFilterRef.current = null;
            setBlurOn(false);
        } finally {
            setBlurBusy(false);
        }
    };

    /**
     * Toggle RNNoise suppression on the outgoing audio track. Same
     * shape as toggleBlur: stash the original mic track once, build
     * the filter on first enable, swap on every peer connection.
     * The original mic track stays untouched across toggles so the
     * OS privacy indicator doesn't flicker.
     */
    const noiseBusyRef = useRef(false);
    /**
     * Turn suppression on or off. Idempotent and safe to call from effects (it reads refs, not
     * stale state). `remember` stores the choice for next time; automatic calls (the join-time
     * enable, re-applying after a microphone switch) pass false so they never overwrite it.
     */
    const setNoiseSuppression = async (on: boolean, remember: boolean) => {
        if (remember) writeNoisePref(on);
        if (noiseBusyRef.current) return;
        const mic = localStreamRef.current?.getAudioTracks()[0];
        if (!mic) return;
        const isOn = noiseSuppressorRef.current !== null;
        if (isOn === on) return;
        noiseBusyRef.current = true;
        setNoiseBusy(true);
        try {
            if (on) {
                if (!originalMicTrackRef.current) originalMicTrackRef.current = mic;
                const ns = new NoiseSuppressor(originalMicTrackRef.current);
                const out = await ns.start();
                // The filtered track is a new track and starts enabled: carry the mute state over,
                // or someone who joined muted is heard the moment suppression switches on.
                out.enabled = mic.enabled;
                noiseSuppressorRef.current = ns;
                replaceLocalAudioTrack(out);
                setNoiseSuppressorOn(true);
            } else {
                noiseSuppressorRef.current?.stop();
                noiseSuppressorRef.current = null;
                if (originalMicTrackRef.current) {
                    originalMicTrackRef.current.enabled = mic.enabled;
                    replaceLocalAudioTrack(originalMicTrackRef.current);
                }
                setNoiseSuppressorOn(false);
            }
        } catch (err) {
            // The browser's own suppression (set at capture) still applies; say so rather than
            // leaving the person guessing why the toggle snapped back.
            setMediaError(err instanceof Error ? `Couldn't enable noise suppression: ${err.message}` : "Couldn't enable noise suppression.");
            noiseSuppressorRef.current?.stop();
            noiseSuppressorRef.current = null;
            setNoiseSuppressorOn(false);
        } finally {
            noiseBusyRef.current = false;
            setNoiseBusy(false);
        }
    };
    const toggleNoiseSuppressor = () => setNoiseSuppression(noiseSuppressorRef.current === null, true);

    // On by default: as soon as a microphone is live (and again after a microphone switch hands
    // us a new raw track), apply the stored preference. Failure leaves the raw track in place.
    useEffect(() => {
        if (!readNoisePref()) return;
        if (noiseSuppressorRef.current !== null || noiseBusyRef.current) return;
        if (!localStreamRef.current?.getAudioTracks()[0]) return;
        void setNoiseSuppression(true, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localStream]);

    /**
     * Replace the outgoing audio track on every peer connection AND on
     * the local preview MediaStream. Used by the noise-suppressor
     * toggle.
     */
    const replaceLocalAudioTrack = (next: MediaStreamTrack) => {
        for (const link of Object.values(linksRef.current)) {
            if (link.audioSender) void link.audioSender.replaceTrack(next);
            // No microphone when this connection was made: add it now (renegotiates).
            else link.audioSender = link.pc.addTrack(next, localStreamRef.current ?? new MediaStream([next]));
        }
        if (localStreamRef.current) {
            const old = localStreamRef.current.getAudioTracks()[0];
            if (old && old !== next) localStreamRef.current.removeTrack(old);
            localStreamRef.current.addTrack(next);
            // The audio track on the preview stream is invisible (the
            // <video> element is muted) but rebinding ensures the mute
            // toggle keeps targeting the correct track.
            setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
        }
    };

    /**
     * Replace the outgoing video track on every peer connection AND
     * on the local preview MediaStream so the user sees what peers
     * see. Used by the blur toggle and by screen-share toggle.
     */
    const replaceLocalVideoTrack = (next: MediaStreamTrack) => {
        // The CAMERA sender — while presenting, the screen travels on its own sender.
        for (const link of Object.values(linksRef.current)) {
            // Outside the video set the camera sender stays empty; the effect below puts the track back on rotation.
            if (link.cameraSender) void link.cameraSender.replaceTrack(canSendVideo() ? next : null).catch(() => undefined);
            else if (!isAudio) {
                link.cameraSender = link.pc.addTrack(next, localStreamRef.current ?? new MediaStream([next]));
                if (!canSendVideo()) void link.cameraSender.replaceTrack(null).catch(() => undefined);
            }
        }
        if (localStreamRef.current) {
            const old = localStreamRef.current.getVideoTracks()[0];
            if (old && old !== next) localStreamRef.current.removeTrack(old);
            localStreamRef.current.addTrack(next);
            // Force a re-render with the new MediaStream reference so
            // the LocalTile <video> element rebinds to the filtered
            // track (without this, the preview keeps showing the
            // unfiltered camera even though peers see the blurred one).
            setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
        }
    };

    // Tear down filters on unmount so the OS releases the workers.
    useEffect(() => () => {
        noiseSuppressorRef.current?.stop();
        noiseSuppressorRef.current = null;
        blurFilterRef.current?.stop();
        blurFilterRef.current = null;
        shareStreamRef.current?.getTracks().forEach((t) => t.stop());
        shareStreamRef.current = null;
    }, []);

    // Classic video picture-in-picture — the fallback where the Document PiP pop-out isn't available
    // (Firefox, Safari). Shows the active speaker's video (or the only remote one / the shared screen).
    const togglePip = async () => {
        try {
            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
                setPipOn(false);
                return;
            }
            const speaker = activeSpeakerIdRef.current;
            const video = (speaker ? document.querySelector<HTMLVideoElement>(`video[data-peer-video="${CSS.escape(speaker)}"]`) : null)
                ?? document.querySelector<HTMLVideoElement>("video[data-pip-target]");
            if (!video) return;
            await video.requestPictureInPicture();
            setPipOn(true);
            video.addEventListener("leavepictureinpicture", () => setPipOn(false), { once: true });
        } catch {
            // Unsupported browser, no user gesture, or the video isn't playing yet — ignore.
        }
    };

    const stopShareCapture = () => {
        shareStreamRef.current?.getTracks().forEach((t) => t.stop());
        shareStreamRef.current = null;
        setShareStream(null);
    };

    const stopSharing = () => {
        for (const link of Object.values(linksRef.current)) {
            if (link.screenSender) {
                try { link.pc.removeTrack(link.screenSender); } catch { /* closed */ }
                link.screenSender = null;
            }
        }
        sharingRef.current = false;
        for (const link of Object.values(linksRef.current)) {
            applyCaps(link);
        }
        // Stop the capture so the browser's "sharing your screen" bar goes away.
        stopShareCapture();
        setSharing(false);
        sharingRef.current = false;
        setPopoutPrompt(false);
        void hubRef.current?.screenShareStream(false, null);
    };

    const toggleShare = async () => {
        if (sharing) {
            stopSharing();
            return;
        }
        try {
            const display = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    // Prefer crisp text/UI over motion smoothness —
                    // matches Teams' screen-share preset.
                    frameRate: { ideal: 15, max: 30 },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                },
                // Don't offer this meeting tab itself: sharing it sends the meeting into itself (the
                // "hall of mirrors"). Other tabs, windows and whole screens stay available.
                selfBrowserSurface: "exclude",
                surfaceSwitching: "include",
                monitorTypeSurfaces: "include"
            } as DisplayMediaStreamOptions);
            const shareTrack = display.getVideoTracks()[0];
            if (!shareTrack) return;

            // contentHint: 'detail' tells the encoder to prioritise
            // sharpness (text, UI elements) over motion smoothness.
            // The hint is non-standard but Chromium and Firefox both
            // honour it, which makes a significant difference for
            // slides and IDE windows.
            (shareTrack as MediaStreamTrack & { contentHint?: string }).contentHint = "detail";

            // "Stop sharing" in the browser's bar.
            shareTrack.onended = () => stopSharing();
            shareStreamRef.current = display;
            setShareStream(display);
            setSharing(true);
            sharingRef.current = true;
            // The screen goes out as a SECOND video track next to the camera (it used to REPLACE the camera,
            // so the presenter vanished from everyone's participant strip). Each connection renegotiates to
            // add it; the stream id tells receivers which stream is the screen.
            if (!isAudio) {
                for (const link of Object.values(linksRef.current)) {
                    try { link.screenSender = link.pc.addTrack(shareTrack, display); } catch { /* closed */ }
                    applyCaps(link);
                }
            }
            void hub.screenShareStream(true, display.id);
            // Google Meet's pop-out: keep the call in view while presenting another tab or window.
            if (isDocumentPipSupported() && !pipWinRef.current) {
                void openPopout(false).then((ok) => { if (!ok) setPopoutPrompt(true); });
            }
        } catch {
            /* user cancelled */
        }
    };

    // Guests post plain text through the hub (they have no token); the server
    // stores it with the meeting chat. Signed-in people use the full composer.
    const sendGuestChat = (e: React.FormEvent) => {
        e.preventDefault();
        const msg = chatDraft.trim();
        if (!msg) return;
        void hub.sendChat(msg);
        setChatDraft("");
    };

    // ── Recording (Teams-style, host only) ──────────────────────────────
    // The host's browser records the meeting TAB (getDisplayMedia, offering
    // the current tab first) with one audio track mixed in an AudioContext
    // from the host's mic and every participant's incoming audio — people
    // who join mid-recording are added as they arrive. The tab's own audio
    // is deliberately NOT mixed in: it is those same voices played back, and
    // mixing both doubles every remote speaker into an echo. On stop,
    // recordingSaver uploads the file to the host's Drive ("Recordings"),
    // shares it with the attendees and registers it on the meeting — and it
    // keeps going after this room closes.

    const recordings = useMeetingRecordings(meetingId, !isGuest);

    const syncRecordingAudio = useCallback((peersNow: Peer[]) => {
        const ctx = audioCtxRef.current;
        const dest = audioDestRef.current;
        if (!ctx || !dest) return;
        const wanted = new Map<string, MediaStreamTrack>();
        for (const t of localStreamRef.current?.getAudioTracks() ?? []) wanted.set(t.id, t);
        for (const p of peersNow) for (const t of p.stream?.getAudioTracks() ?? []) wanted.set(t.id, t);
        const sources = audioSourcesRef.current;
        for (const [id, node] of sources) {
            if (!wanted.has(id)) {
                node.disconnect();
                sources.delete(id);
            }
        }
        for (const [id, track] of wanted) {
            if (sources.has(id) || track.readyState !== "live") continue;
            try {
                const node = ctx.createMediaStreamSource(new MediaStream([track]));
                node.connect(dest);
                sources.set(id, node);
            } catch { /* a track that ended between the check and here */ }
        }
    }, []);

    // Newcomers' audio (and a switched or noise-suppressed mic) joins the mix.
    useEffect(() => {
        if (recording) syncRecordingAudio(Object.values(peers));
    }, [recording, peers, localStream, syncRecordingAudio]);

    const releaseCapture = () => {
        captureStreamRef.current?.getTracks().forEach((t) => t.stop());
        captureStreamRef.current = null;
        for (const node of audioSourcesRef.current.values()) node.disconnect();
        audioSourcesRef.current.clear();
        void audioCtxRef.current?.close().catch(() => undefined);
        audioCtxRef.current = null;
        audioDestRef.current = null;
    };

    /** Everyone to share the recording with: invitees, whoever joined, and whoever is here now (guests have no email). */
    const collectAttendeeEmails = async (): Promise<string[]> => {
        const emails = new Set<string>();
        for (const p of Object.values(peersRef.current)) if (p.email) emails.add(p.email);
        const token = tokenRef.current;
        const [invitees, joined] = await Promise.all([
            connectParityApi.meetingInvitees(meetingId, token).catch(() => []),
            connectParityApi.meetingParticipants(meetingId, token).catch(() => [])
        ]);
        for (const a of invitees) if (a.email) emails.add(a.email);
        for (const p of joined) if (p.email) emails.add(p.email);
        return [...emails];
    };

    // Runs from MediaRecorder.onstop — possibly after the room has unmounted,
    // so it only touches refs and the module-level saver.
    const finishRecording = (mimeType: string) => {
        const chunks = recordedChunks.current;
        recordedChunks.current = [];
        const startedAt = recordStartedAt.current ?? new Date();
        recordStartedAt.current = null;
        recorderRef.current = null;
        releaseCapture();
        const blob = new Blob(chunks, { type: mimeType.split(";")[0] || "video/webm" });
        if (blob.size === 0) {
            setRecordingNotice("The recording was empty, so nothing was saved.");
            return;
        }
        recordingSaver.save({
            blob,
            fileName: recordingFileName(meetingSubject, startedAt),
            meetingId,
            durationSeconds: (Date.now() - startedAt.getTime()) / 1000,
            ownerEmail: auth.user?.profile.email ?? "",
            collectEmails: collectAttendeeEmails
        });
    };

    const stopRecording = () => {
        const r = recorderRef.current;
        if (!r || r.state === "inactive") return;
        r.stop();
        setRecording(false);
        void hubRef.current?.setRecordingState(false);
    };

    const startRecording = async () => {
        if (!isHost || !allowRecording || recorderRef.current || recordingBusy) return;
        setRecordingBusy(true);
        let display: MediaStream;
        try {
            display = await navigator.mediaDevices.getDisplayMedia({
                video: { displaySurface: "browser" },
                audio: false,
                preferCurrentTab: true,
                selfBrowserSurface: "include"
            } as DisplayMediaStreamOptions);
        } catch {
            // The host closed the picker (or the browser refused).
            setRecordingBusy(false);
            setRecordingNotice("Recording not started.");
            return;
        }
        const video = display.getVideoTracks()[0];
        if (!video || typeof MediaRecorder === "undefined") {
            display.getTracks().forEach((t) => t.stop());
            setRecordingBusy(false);
            setRecordingNotice("Recording not started — this browser can't record the meeting.");
            return;
        }
        captureStreamRef.current = display;
        const ctx = new AudioContext();
        const dest = ctx.createMediaStreamDestination();
        audioCtxRef.current = ctx;
        audioDestRef.current = dest;
        void ctx.resume().catch(() => undefined);
        syncRecordingAudio(Object.values(peersRef.current));

        const mixed = new MediaStream([video, ...dest.stream.getAudioTracks()]);
        const mime = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
            .find((m) => MediaRecorder.isTypeSupported(m));
        let recorder: MediaRecorder;
        try {
            recorder = new MediaRecorder(mixed, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
        } catch {
            releaseCapture();
            setRecordingBusy(false);
            setRecordingNotice("Recording not started — the browser refused to record.");
            return;
        }
        recordedChunks.current = [];
        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) recordedChunks.current.push(e.data);
        };
        recorder.onstop = () => finishRecording(recorder.mimeType || mime || "video/webm");
        // "Stop sharing" in the browser's bar ends the recording.
        video.addEventListener("ended", () => stopRecording(), { once: true });
        recorderRef.current = recorder;
        recordStartedAt.current = new Date();
        // 5 s slices: a crash loses at most the last few seconds' chunk.
        recorder.start(5000);
        setRecording(true);
        setRecordingBusy(false);
        void hub.setRecordingState(true);
    };

    const toggleRecording = () => {
        if (recording) stopRecording(); else void startRecording();
    };

    // Closing the tab mid-recording would lose it; the browser asks first.
    useEffect(() => {
        if (!recording) return;
        const onUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onUnload);
        return () => window.removeEventListener("beforeunload", onUnload);
    }, [recording]);

    useEffect(() => {
        if (!recordingNotice) return;
        const t = window.setTimeout(() => setRecordingNotice(null), 10_000);
        return () => window.clearTimeout(t);
    }, [recordingNotice]);

    const leave = () => {
        // Stops and saves a recording in progress (the save outlives the room).
        stopRecording();
        // Hanging up a call nobody answered stops it ringing on their side —
        // it used to ring on for the full 45 s.
        if (placingCall && isHost && !everHadPeerRef.current) {
            void connectParityApi.endMeeting(meetingId, accessToken).catch(() => undefined);
        }
        if (joinedRef.current) void hub.leaveMeeting(meetingId);
        pipWinRef.current?.close();
        onLeave();
    };

    const endForEveryone = () => {
        void hub.endMeeting();
        void connectParityApi.endMeeting(meetingId, accessToken).catch(() => undefined);
        leave();
    };

    // While recording, Leave / End first confirms that the recording will stop and be saved.
    const requestLeave = (mode: "leave" | "end") => {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            setConfirmLeave(mode);
            return;
        }
        if (mode === "end") endForEveryone(); else leave();
    };

    const retryCall = async () => {
        declinedCountRef.current = 0;
        setOutgoing("ringing");
        try {
            await connectParityApi.startMeeting(meetingId, accessToken);
        } catch { /* the screen still shows it ringing */ }
    };

    const copyJoinLink = async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/guest/${meetingId}`);
        } catch { /* clipboard blocked */ }
    };

    /** Switch camera, mic or speaker mid-call; peers get the new track without renegotiating. */
    const switchDevice = async (kindToSwitch: DeviceKind, deviceId: string) => {
        if (kindToSwitch === "speaker") {
            setActiveDevices((d) => ({ ...d, speakerId: deviceId }));
            return;
        }
        try {
            if (kindToSwitch === "camera") {
                if (blurOn) await toggleBlur();
                const s = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
                });
                const track = s.getVideoTracks()[0];
                if (!track) return;
                track.enabled = cameraOn;
                const old = localStreamRef.current?.getVideoTracks()[0];
                originalCamTrackRef.current = null;
                // The camera has its own sender even while presenting, so this never touches the screen.
                replaceLocalVideoTrack(track);
                old?.stop();
                setActiveDevices((d) => ({ ...d, cameraId: deviceId }));
            } else {
                // Pause the filter on the old mic without touching the saved preference; the
                // effect on `localStream` re-applies it to the new mic's raw track.
                if (noiseSuppressorRef.current !== null) await setNoiseSuppression(false, false);
                const s = await navigator.mediaDevices.getUserMedia({
                    audio: { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
                });
                const track = s.getAudioTracks()[0];
                if (!track) return;
                track.enabled = micOn;
                const old = localStreamRef.current?.getAudioTracks()[0];
                originalMicTrackRef.current = null;
                replaceLocalAudioTrack(track);
                old?.stop();
                setActiveDevices((d) => ({ ...d, micId: deviceId }));
            }
        } catch (err) {
            setMediaError(err instanceof Error ? err.message : "Couldn't switch to that device.");
        }
    };

    // --- Host actions ---------------------------------------------------

    const setLayout = (mode: LayoutMode) => {
        setLayoutMode(mode);
        void hub.setLayout(mode);
    };

    // Confirmed in the bar above the toolbar — a blocking browser dialog used to ask.
    const muteAll = () => {
        setConfirmMuteAll(false);
        void hub.muteAll();
    };

    const toggleSpotlight = (connectionId: string) => {
        const next = spotlightConnectionId === connectionId ? null : connectionId;
        setSpotlightConnectionId(next);
        if (next) {
            void hub.setSpotlight(next);
            if (layoutMode === "gallery") setLayout("spotlight");
        } else {
            void hub.clearSpotlight();
        }
    };

    // Confirmed inline in the People pane.
    const forceMutePeer = (connectionId: string) => {
        void hub.forceMuteOne(connectionId);
    };

    // --- Render ---------------------------------------------------------

    // Re-order peer tiles so the spotlighted participant appears first.
    // Spotlight + Focus layouts both promote the spotlit tile to the
    // primary stage; Gallery preserves natural join order.
    const peerList = useMemo(() => {
        const all = Object.values(peers);
        if (!spotlightConnectionId) return all;
        const idx = all.findIndex((p) => p.connectionId === spotlightConnectionId);
        if (idx < 0) return all;
        const spot = all[idx]!;
        return [spot, ...all.filter((_, i) => i !== idx)];
    }, [peers, spotlightConnectionId]);
    roomSizeRef.current = peerList.length + 1;

    // ── Speaker-rotated video (FPT-7) ──────────────────────────────────────
    // The hub keeps the six most recent speakers in the video set. Inside it our camera goes out on every
    // connection; outside it the camera sender carries NO track (replaceTrack(null)): the encoder stops and no
    // video bytes leave at all, unlike track.enabled = false, which still encodes and sends black frames. It
    // needs no renegotiation either way, so rotating back in is immediate (the browser sends a key frame).
    // The camera keeps capturing locally, so your preview stays and resuming costs nothing.
    const mayVideo = isAudio || maySendVideo(videoSlots, [hub.connectionId, joinedConnRef.current, prevJoinedConnRef.current]);
    useEffect(() => {
        if (isAudio) return;
        const cam = mayVideo ? localStream?.getVideoTracks()[0] ?? null : null;
        for (const link of Object.values(linksRef.current)) {
            const sender = link.cameraSender;
            if (sender && sender.track !== cam) {
                // Caps after the swap: they are the camera's, and follow the room's size.
                void sender.replaceTrack(cam).then(() => applyCaps(link)).catch(() => undefined);
            } else {
                applyCaps(link);
            }
        }
        // applyCaps reads refs only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAudio, mayVideo, localStream, peerList.length]);

    // Report our own speech so the hub can rotate us in (video meetings on a server that rotates).
    useSpeakingReporter(
        localStream?.getAudioTracks()[0] ?? null,
        !isAudio && micOn && videoSlots !== null,
        () => void hubRef.current?.reportSpeaking());

    // Developer measurement of upload per peer and in total (off unless switched on; see uploadStats.ts).
    useUploadLog(
        () => peerConnections.current,
        () => Object.fromEntries(Object.values(peersRef.current).map((p) => [p.connectionId, p.displayName || p.email])));

    // Receivers: video tiles for the people in the set, an audio list for everyone else.
    const videoSet = useMemo(() => (videoSlots === null ? null : new Set(videoSlots)), [videoSlots]);
    const onVideo = (p: Peer) => isAudio || videoSet === null || videoSet.has(p.connectionId);
    // A spotlighted person keeps their tile (as an avatar) even while they are on audio.
    const tilePeers = peerList.filter((p) => onVideo(p) || p.connectionId === spotlightConnectionId);
    const audioListPeers = isAudio ? [] : peerList.filter((p) => !onVideo(p) && p.connectionId !== spotlightConnectionId);
    const selfOnAudio = !isAudio && cameraOn && !mayVideo;

    // Screen-share stage: while anyone presents, the shared screen fills the
    // main area, everyone else stacks in a vertical rail on the right and the
    // chat panel narrows — Teams-style max use of the share surface. Local
    // share wins over a remote's if both happen simultaneously.
    const sharingPeer = useMemo(
        () => peerList.find((p) => p.sharing) ?? null,
        [peerList]
    );
    const shareActive = !isAudio && (sharing || sharingPeer !== null);

    const sameUser = (a: string, b: string) => normalizeUserId(a) === normalizeUserId(b);
    const hostPresent = hostUserId !== undefined
        && (isHost || peerList.some((p) => sameUser(p.userId, hostUserId)));
    // Mirrors the server: the host admits, or any signed-in person while the host is away.
    const canAdmit = !isGuest && (hostUserId === undefined || isHost || !hostPresent);
    const rosterPeople: RosterPerson[] = [
        {
            connectionId: "self",
            name: selfDisplayName,
            isYou: true,
            isHost,
            isGuest,
            micOn,
            camOn: cameraOn,
            handRaised,
            raisedAt: handRaisedAt,
            sharing
        },
        ...peerList.map((p) => ({
            connectionId: p.connectionId,
            name: p.displayName || p.email || "Participant",
            isHost: hostUserId !== undefined && sameUser(p.userId, hostUserId),
            isGuest: p.userId.startsWith("guest:"),
            micOn: p.audio,
            camOn: p.video && p.stream !== null && onVideo(p),
            handRaised: p.handRaised,
            raisedAt: p.raisedAt ?? null,
            sharing: p.sharing,
            spotlighted: spotlightConnectionId === p.connectionId
        }))
    ];
    const admitAll = () => {
        for (const k of lobbyKnockers) void hub.admitFromLobby(meetingId, k.connectionId);
    };

    // Someone left, so there is room for video again.
    useEffect(() => {
        if (videoLimited && peerList.length + 1 <= maxVideo) setVideoLimited(false);
    }, [peerList.length, videoLimited, maxVideo]);

    // Opening the chat clears its badge.
    useEffect(() => {
        if (chatOpen) setChatUnread(0);
    }, [chatOpen]);

    // "The host lowered your hand" fades on its own.
    useEffect(() => {
        if (!handNotice) return;
        const t = window.setTimeout(() => setHandNotice(null), 5000);
        return () => window.clearTimeout(t);
    }, [handNotice]);

    // Teams' "This meeting ends in 5 minutes": a notice once, near the scheduled
    // end, and one when it passes — never a kick. Joining after the end is silent.
    useEffect(() => {
        const end = scheduledEnd ? Date.parse(scheduledEnd) : NaN;
        if (!Number.isFinite(end)) return;
        let warned = false;
        let over = false;
        const tick = () => {
            const left = end - Date.now();
            if (!warned && left > 0 && left <= 5 * 60_000) {
                warned = true;
                const mins = Math.max(1, Math.ceil(left / 60_000));
                setEndNotice(`This meeting ends in ${mins} minute${mins === 1 ? "" : "s"}.`);
            }
            if (!over && left <= 0 && left > -60_000) {
                over = true;
                setEndNotice("The meeting's scheduled time is up. You can keep going — nobody will be removed.");
            }
        };
        tick();
        const t = window.setInterval(tick, 15_000);
        return () => window.clearInterval(t);
    }, [scheduledEnd]);

    // A guest's chat log follows new messages.
    const guestLogRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = guestLogRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [guestChat.length, chatOpen]);

    // A call nobody answers stops ringing after 45 s on both sides.
    useEffect(() => {
        if (outgoing !== "ringing") return;
        const t = window.setTimeout(() => {
            if (everHadPeerRef.current) return;
            setOutgoing("no-answer");
            void connectParityApi.endMeeting(meetingId, accessToken).catch(() => undefined);
        }, 45_000);
        return () => window.clearTimeout(t);
    }, [outgoing, meetingId, accessToken]);

    // ── Per-peer media health + active speaker ─────────────────────────────
    const health = usePeerHealth(() => peerConnections.current);
    const lastSpeakerRef = useRef<string | null>(null);
    const activeSpeakerId = useMemo(() => {
        let best: string | null = null;
        let loudest = 0.04;
        for (const p of peerList) {
            const h = health[p.connectionId];
            if (h && p.audio && h.audioLevel > loudest) {
                loudest = h.audioLevel;
                best = p.connectionId;
            }
        }
        // Sticky: the last person who spoke stays "active" through pauses.
        if (best) lastSpeakerRef.current = best;
        if (lastSpeakerRef.current && !peers[lastSpeakerRef.current]) lastSpeakerRef.current = null;
        return lastSpeakerRef.current;
    }, [health, peerList, peers]);
    const activeSpeakerIdRef = useRef(activeSpeakerId);
    activeSpeakerIdRef.current = activeSpeakerId;

    // ── Reconnecting: hub down, or connected again but not yet back in the room ──
    // Nobody is removed on a drop: peer connections keep the media flowing while the hub reconnects and
    // the room rejoins. After two minutes, offer a manual Rejoin (retries continue underneath).
    const reconnecting = everInRoom && lobbyState === "idle" && (hub.state !== HubConnectionState.Connected || !inRoom);
    const [lostTooLong, setLostTooLong] = useState(false);
    useEffect(() => {
        if (!reconnecting) {
            setLostTooLong(false);
            return;
        }
        const t = window.setTimeout(() => setLostTooLong(true), RECONNECT_TIMEOUT_MS);
        return () => window.clearTimeout(t);
    }, [reconnecting]);
    const rejoinNow = () => {
        setLostTooLong(false);
        void hubRef.current?.restart();
    };

    // ── Remote audio blocked by the browser's autoplay policy ─────────────
    const [audioBlocked, setAudioBlocked] = useState(false);
    const onAudioBlocked = useCallback(() => setAudioBlocked(true), []);
    const enableAudio = useCallback(() => {
        const els = Array.from(document.querySelectorAll<HTMLAudioElement>("audio[data-remote-audio]"));
        void Promise.all(els.map((el) => el.play()))
            .then(() => setAudioBlocked(false))
            .catch(() => undefined);
    }, []);
    useEffect(() => {
        if (!audioBlocked) return;
        // Any click or key press in the page is the gesture the browser is waiting for.
        window.addEventListener("pointerdown", enableAudio);
        window.addEventListener("keydown", enableAudio);
        return () => {
            window.removeEventListener("pointerdown", enableAudio);
            window.removeEventListener("keydown", enableAudio);
        };
    }, [audioBlocked, enableAudio]);

    // ── Pop-out (Document Picture-in-Picture) ─────────────────────────────
    const docPipSupported = isDocumentPipSupported();
    const [pipWin, setPipWin] = useState<Window | null>(null);
    const pipWinRef = useRef<Window | null>(null);
    // Opened by switching tabs (closed again on coming back), vs. opened on purpose.
    const autoOpenedRef = useRef(false);
    const autoVideoPipRef = useRef(false);
    const [popoutPrompt, setPopoutPrompt] = useState(false);
    const [autoPopout, setAutoPopout] = useState(readAutoPopout);
    const setAutoPopoutPref = (on: boolean) => {
        setAutoPopout(on);
        try { window.localStorage.setItem(AUTO_POPOUT_KEY, on ? "1" : "0"); } catch { /* private mode */ }
    };
    const togglePipRef = useRef(togglePip);
    togglePipRef.current = togglePip;

    const openPopout = useCallback(async (auto: boolean): Promise<boolean> => {
        if (!isDocumentPipSupported()) return false;
        if (pipWinRef.current) return true;
        try {
            const win = await openDocumentPip(meetingSubject);
            pipWinRef.current = win;
            autoOpenedRef.current = auto;
            // Closing the window (its own ✕ or "back to tab") just returns to the normal layout.
            win.addEventListener("pagehide", () => {
                if (pipWinRef.current !== win) return;
                pipWinRef.current = null;
                autoOpenedRef.current = false;
                setPipWin(null);
            }, { once: true });
            setPipWin(win);
            setPopoutPrompt(false);
            return true;
        } catch {
            // No user activation (or the user dismissed it).
            return false;
        }
    }, [meetingSubject]);

    const closePopout = useCallback(() => {
        const win = pipWinRef.current;
        pipWinRef.current = null;
        autoOpenedRef.current = false;
        setPipWin(null);
        win?.close();
    }, []);
    useEffect(() => () => { pipWinRef.current?.close(); }, []);

    // "Show floating window when I switch tabs".
    useEffect(() => {
        if (!autoPopout || !everInRoom) return;
        const onVisibility = () => {
            if (document.hidden) {
                if (pipWinRef.current || document.pictureInPictureElement) return;
                void openPopout(true).then((ok) => {
                    if (ok || isDocumentPipSupported() || isAudio) return;
                    // Classic picture-in-picture of the active speaker (where the browser allows it without a click).
                    void togglePipRef.current().then(() => { autoVideoPipRef.current = Boolean(document.pictureInPictureElement); });
                });
            } else {
                if (autoOpenedRef.current) closePopout();
                if (autoVideoPipRef.current && document.pictureInPictureElement) {
                    autoVideoPipRef.current = false;
                    void document.exitPictureInPicture().catch(() => undefined);
                    setPipOn(false);
                }
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        // Chrome's automatic picture-in-picture for video calls: on a tab switch it invokes this media-session
        // action WITH the user activation that requestWindow() needs (a bare visibilitychange has none).
        const ms: MediaSession | undefined = "mediaSession" in navigator ? navigator.mediaSession : undefined;
        const action = "enterpictureinpicture" as unknown as MediaSessionAction;
        let registered = false;
        try {
            ms?.setActionHandler(action, () => { void openPopout(true); });
            registered = ms !== undefined;
        } catch { /* action not supported by this browser */ }
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            if (registered) {
                try { ms?.setActionHandler(action, null); } catch { /* ignore */ }
            }
        };
    }, [autoPopout, everInRoom, openPopout, closePopout, isAudio]);

    const popoutTiles = useMemo(() => {
        const self: PopoutTile = {
            id: "self", label: "You", stream: localStream, videoOn: cameraOn && localStream !== null,
            muted: !micOn, handRaised, speaking: false, isSelf: true
        };
        const remote: PopoutTile[] = peerList.map((p) => ({
            id: p.connectionId,
            label: p.displayName || p.email || "Participant",
            stream: p.stream,
            videoOn: p.video && p.stream !== null && onVideo(p),
            muted: !p.audio,
            handRaised: p.handRaised,
            speaking: p.connectionId === activeSpeakerId
        }));
        const presenterId = sharingPeer && !sharing ? sharingPeer.connectionId : null;
        const mainId = activeSpeakerId ?? presenterId ?? remote[0]?.id ?? null;
        const main = remote.find((t) => t.id === mainId) ?? self;
        const others = [...remote.filter((t) => t.id !== main.id), ...(main.isSelf ? [] : [self])].slice(0, 3);
        return { main, others };
        // onVideo reads videoSet and isAudio.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [peerList, activeSpeakerId, sharingPeer, sharing, localStream, cameraOn, micOn, handRaised, videoSet]);

    // Lobby waiting screen — shown while the local user hasn't been admitted.
    if (lobbyState === "waiting") {
        return (
            <div className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-6 bg-zinc-950 text-white">
                <div className="flex flex-col items-center gap-3 rounded-xl bg-zinc-900 px-10 py-8 shadow-2xl text-center max-w-sm w-full mx-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-700 text-2xl font-semibold">
                        {selfDisplayName.charAt(0).toUpperCase()}
                    </div>
                    <h2 className="text-lg font-semibold">Waiting in lobby</h2>
                    <p className="text-sm text-white/60">
                        Someone will let you in soon. Please wait.
                    </p>
                    <p className="text-xs text-white/40 font-medium">{meetingSubject}</p>
                    <div className="flex gap-1 mt-1">
                        {[0, 0.15, 0.3].map((delay, i) => (
                            <span
                                key={i}
                                className="inline-block h-2 w-2 rounded-full bg-primary animate-pulse"
                                style={{ animationDelay: `${delay}s` }}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={onLeave}
                        className="mt-2 flex h-9 items-center gap-1.5 rounded-md bg-destructive px-4 text-sm font-semibold text-destructive-foreground"
                    >
                        <PhoneOff className="h-4 w-4" /> Cancel
                    </button>
                </div>
            </div>
        );
    }

    // Turned away because the host locked the meeting.
    if (lockedOut) {
        return (
            <div className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-zinc-950 text-white">
                <div className="mx-4 flex w-full max-w-sm flex-col items-center gap-3 rounded-xl bg-zinc-900 px-10 py-8 text-center shadow-2xl">
                    <Lock className="h-10 w-10 text-amber-300" aria-hidden />
                    <h2 className="text-lg font-semibold">{meetingFull ? "This meeting is full" : "This meeting is locked"}</h2>
                    <p className="text-sm text-white/60">{meetingFull ? `${meetingSubject} has reached the number of people your organisation allows. Try again when someone leaves.` : `The host locked ${meetingSubject}, so nobody new can join. Ask the host to unlock it, then try again.`}</p>
                    <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => window.location.reload()} className="h-9 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground">Try again</button>
                        <button type="button" onClick={onLeave} className="h-9 rounded-md bg-white/10 px-4 text-sm hover:bg-white/20">Close</button>
                    </div>
                </div>
            </div>
        );
    }

    // Lobby denied screen
    if (lobbyState === "denied") {
        return (
            <div className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-zinc-950 text-white">
                <div className="flex flex-col items-center gap-3 rounded-xl bg-zinc-900 px-10 py-8 shadow-2xl text-center max-w-sm w-full mx-4">
                    <PhoneOff className="h-10 w-10 text-red-400" />
                    <h2 className="text-lg font-semibold">Entry denied</h2>
                    <p className="text-sm text-white/60">The host did not admit you to this meeting.</p>
                    <button
                        type="button"
                        onClick={onLeave}
                        className="mt-2 h-9 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <SpeakerContext.Provider value={activeDevices.speakerId}>
        <div className="fixed inset-0 z-40 flex flex-col bg-zinc-950 text-white">
            <header className="flex h-12 shrink-0 items-center justify-between border-b border-white/10 px-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{meetingSubject}</span>
                    <span className="text-xs text-white/60">{peerList.length + 1} participant{peerList.length === 0 ? "" : "s"}</span>
                    {meetingLocked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/80" title="The meeting is locked: nobody new can join">
                            <Lock className="h-3 w-3" aria-hidden /> Locked
                        </span>
                    ) : null}
                    {isHost ? (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-300"
                            title="You are hosting this meeting"
                        >
                            <Crown className="h-3 w-3" /> Host
                        </span>
                    ) : null}
                    {roomRecording || recording ? (
                        <span
                            role="status"
                            className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white"
                            title={recording ? "You are recording this meeting" : `Recorded by ${roomRecording?.byDisplayName ?? "the organizer"}`}
                            aria-label="This meeting is being recorded"
                        >
                            <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-white" aria-hidden />
                            Recording
                        </span>
                    ) : null}
                    {/* Connection quality indicator — 3-bar signal icon */}
                    <ConnectionQualityIndicator
                        pcs={peerConnections.current}
                        className="ml-2 text-white/80"
                    />
                </div>
                <div className="flex items-center gap-1">
                    <CallTimer scheduledEnd={scheduledEnd ?? null} />
                    <button
                        type="button"
                        onClick={() => { setRosterOpen((v) => !v); setChatOpen(false); setInviteOpen(false); }}
                        aria-pressed={rosterOpen}
                        aria-label={`People (${peerList.length + 1})${canAdmit && lobbyKnockers.length > 0 ? `, ${lobbyKnockers.length} waiting` : ""}`}
                        title="People"
                        className={cn(
                            "flex h-8 items-center gap-1.5 rounded-md px-2 text-xs hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            rosterOpen && "bg-white/10"
                        )}
                    >
                        <Users className="h-4 w-4" />
                        {peerList.length + 1}
                        {canAdmit && lobbyKnockers.length > 0 ? (
                            <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-zinc-900">{lobbyKnockers.length}</span>
                        ) : null}
                    </button>
                </div>
            </header>

            {mediaError ? (
                <p className="shrink-0 bg-destructive/20 px-4 py-2 text-sm text-destructive-foreground">
                    {mediaError}
                </p>
            ) : null}

            {reconnecting ? (
                <div role="status" className="flex shrink-0 flex-wrap items-center gap-2 border-b border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-sm text-amber-100">
                    {lostTooLong
                        ? <WifiOff className="h-4 w-4 shrink-0 text-amber-300" aria-hidden />
                        : <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-300" aria-hidden />}
                    <span className="min-w-0 flex-1">
                        {lostTooLong
                            ? "We couldn't get you back into the meeting. Your call may still be connected — try rejoining."
                            : "Reconnecting to the meeting… Your audio and video keep going while we reconnect."}
                    </span>
                    {lostTooLong ? (
                        <button
                            type="button"
                            onClick={rejoinNow}
                            className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2.5 py-0.5 text-xs font-semibold text-zinc-900 hover:bg-amber-400"
                        >
                            <RefreshCw className="h-3.5 w-3.5" /> Rejoin
                        </button>
                    ) : null}
                </div>
            ) : null}

            {audioBlocked ? (
                <div role="alert" className="flex shrink-0 flex-wrap items-center gap-2 border-b border-primary/40 bg-primary/20 px-4 py-1.5 text-sm text-white">
                    <Volume2 className="h-4 w-4 shrink-0 text-sky-300" aria-hidden />
                    <span className="min-w-0 flex-1">Your browser paused the meeting audio.</span>
                    <button
                        type="button"
                        onClick={enableAudio}
                        className="rounded-md bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                    >
                        Click to enable audio
                    </button>
                </div>
            ) : null}

            {popoutPrompt && sharing && !pipWin ? (
                <div role="status" className="flex shrink-0 flex-wrap items-center gap-2 border-b border-white/10 bg-white/5 px-4 py-1.5 text-sm text-white/90">
                    <AppWindow className="h-4 w-4 shrink-0 text-sky-300" aria-hidden />
                    <span className="min-w-0 flex-1">Keep the call in view while you present.</span>
                    <button
                        type="button"
                        onClick={() => void openPopout(false)}
                        className="rounded-md bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                    >
                        Pop out
                    </button>
                    <button
                        type="button"
                        onClick={() => setPopoutPrompt(false)}
                        aria-label="Dismiss"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-white/10"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            {recordingNotice ? (
                <div role="status" className="flex shrink-0 items-center gap-2 border-b border-red-500/40 bg-red-500/15 px-4 py-2 text-sm text-red-100">
                    <span className="inline-flex h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500" aria-hidden />
                    <span className="min-w-0 flex-1">
                        {recordingNotice}
                        {roomRecording && !recording ? <span className="text-red-200/90"> By staying in the meeting you consent to being recorded.</span> : null}
                    </span>
                    <button
                        type="button"
                        onClick={() => setRecordingNotice(null)}
                        aria-label="Dismiss"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-white/10"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            {endNotice ? (
                <div role="status" className="flex shrink-0 items-center gap-2 border-b border-amber-500/40 bg-amber-500/15 px-4 py-2 text-sm text-amber-100">
                    <Clock className="h-4 w-4 shrink-0 text-amber-300" aria-hidden />
                    <span className="min-w-0 flex-1">{endNotice}</span>
                    <button
                        type="button"
                        onClick={() => setEndNotice(null)}
                        aria-label="Dismiss"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-white/10"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            {handNotice ? (
                <div role="status" className="flex shrink-0 items-center gap-2 border-b border-white/10 bg-white/5 px-4 py-1.5 text-sm text-white/90">
                    <Hand className="h-4 w-4 shrink-0 text-amber-300" aria-hidden />
                    {handNotice}
                </div>
            ) : null}

            {wbInvite && !whiteboardOpen ? (
                <div role="status" className="flex shrink-0 items-center gap-2 border-b border-white/10 bg-primary/15 px-4 py-1.5 text-sm text-white/90">
                    <PencilLine className="h-4 w-4 shrink-0 text-sky-300" aria-hidden />
                    <span className="min-w-0 flex-1">{wbInvite} opened the whiteboard.</span>
                    <button
                        type="button"
                        onClick={() => { setWhiteboardOpen(true); setWbInvite(null); }}
                        className="rounded-md bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                        Open
                    </button>
                    <button
                        type="button"
                        onClick={() => setWbInvite(null)}
                        aria-label="Dismiss"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-white/10"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            <div className={cn(
                // overflow-y-auto: with one column on a phone, three or more
                // tiles are taller than the stage and used to be clipped with
                // no way to reach them.
                "relative min-h-0 flex-1 p-4",
                isAudio
                    ? "flex flex-wrap items-center justify-center gap-6 overflow-y-auto"
                    : shareActive
                        // The screen takes the space; the participant strip sits beside it (under it on a phone).
                        ? "flex flex-col gap-2 overflow-hidden sm:flex-row"
                        : layoutMode === "spotlight"
                            ? "grid grid-cols-1 gap-2 overflow-y-auto lg:grid-cols-[2fr_1fr]"
                            : layoutMode === "focus"
                                ? "grid grid-cols-1 gap-2 overflow-y-auto"
                                : layoutMode === "together"
                                    ? "flex flex-wrap content-center items-end justify-center gap-x-1 gap-y-0 overflow-y-auto [&>*:nth-child(3n)]:-translate-y-3 [&>*:nth-child(even)]:translate-y-6 [&_[data-tile]]:aspect-square [&_[data-tile]]:min-h-0 [&_[data-tile]]:w-[clamp(6rem,15vw,11rem)] [&_[data-tile]]:rounded-full [&_[data-tile]]:border-2 [&_[data-tile]]:border-white/30 [&_[data-tile]]:shadow-xl"
                                    : "grid grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3"
            )} style={layoutMode === "together" && !isAudio && !shareActive ? { background: TOGETHER_SCENES[scene].bg } : undefined}>
                {shareActive ? (
                    /* Share stage — the screen takes everything, people go
                       into the participant strip. */
                    <ShareStage
                        stream={sharing ? shareStream : sharingPeer?.screenStream ?? sharingPeer?.stream ?? null}
                        label={sharing
                            ? "You are presenting"
                            : `${sharingPeer?.displayName || sharingPeer?.email || "Someone"} is presenting`}
                        isSelf={sharing}
                        onStop={sharing ? () => stopSharing() : undefined}
                    />
                ) : null}
                {/* People: display:contents keeps the grid when nothing is shared. During a share this is the
                    participant strip — EVERYONE, the presenter's own camera included (it used to filter the
                    presenter out, leaving only "You"), stacked vertically beside the stage and scrolling
                    vertically only (a fixed 140px tile minimum made tiles wider than the strip: the old
                    horizontal scrollbar). On a phone it's two columns under the stage. */}
                <div className={cn(
                    shareActive
                        ? "max-h-[38%] min-h-0 shrink-0 overflow-y-auto overflow-x-hidden sm:max-h-none sm:w-[clamp(11rem,20vw,17rem)]"
                        : "contents"
                )}>
                <div className={cn(shareActive ? "grid grid-cols-2 gap-2 sm:flex sm:min-h-full sm:flex-col sm:justify-center" : "contents")}>
                <div className={cn("group relative", shareActive && "min-w-0 shrink-0", pinnedTile === "self" && PINNED_TILE, selfHidden && "hidden")}>
                    <PinForMe pinned={pinnedTile === "self"} onToggle={() => setPinnedTile((p) => (p === "self" ? null : "self"))} offset={false} />
                    <LocalTile stream={localStream} micOn={micOn} cameraOn={cameraOn} handRaised={handRaised} sharing={sharing} audioOnly={isAudio} compact={shareActive} />
                    {selfOnAudio ? (
                        <span
                            role="status"
                            className="pointer-events-none absolute left-2 top-2 inline-flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-medium text-white"
                        >
                            <VideoOff className="h-3 w-3 shrink-0" aria-hidden />
                            <span className="truncate">You're on audio until you speak</span>
                        </span>
                    ) : null}
                    {recording ? (
                        <span className="pointer-events-none absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-red-600/90 px-2 py-0.5 text-[10px] font-semibold text-white">
                            <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden />
                            REC
                        </span>
                    ) : null}
                </div>
                {tilePeers.map((peer) => (
                    <div key={peer.connectionId} className={cn("group relative", shareActive && "min-w-0 shrink-0", pinnedTile === peer.connectionId && PINNED_TILE)}>
                        <PinForMe pinned={pinnedTile === peer.connectionId} onToggle={() => setPinnedTile((p) => (p === peer.connectionId ? null : peer.connectionId))} offset={isHost} />
                        <RemoteTile
                            peer={peer}
                            audioOnly={isAudio}
                            compact={shareActive}
                            // An older presenter sends the screen INSTEAD of their camera; it's on the stage already.
                            // Outside the video set (a spotlighted person on audio) there is no camera to show.
                            hideVideo={(shareActive && peer.sharing && !peer.screenStream) || !onVideo(peer)}
                            health={health[peer.connectionId]}
                            speaking={peer.connectionId === activeSpeakerId}
                        />
                        {spotlightConnectionId === peer.connectionId ? (
                            <span className="pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold text-zinc-900">
                                <Pin className="h-3 w-3" /> Spotlighted
                            </span>
                        ) : null}
                        {roomRecording?.byConnectionId === peer.connectionId ? (
                            <span className="pointer-events-none absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-red-600/90 px-2 py-0.5 text-[10px] font-semibold text-white">
                                <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden />
                                REC
                            </span>
                        ) : null}
                        {isHost ? (
                            <div className="absolute right-2 top-2 flex gap-1">
                                <button
                                    type="button"
                                    onClick={() => toggleSpotlight(peer.connectionId)}
                                    aria-label={spotlightConnectionId === peer.connectionId ? "Remove spotlight" : "Spotlight participant"}
                                    title={spotlightConnectionId === peer.connectionId ? "Remove spotlight" : "Spotlight"}
                                    className={cn(
                                        "flex h-7 w-7 items-center justify-center rounded-md text-white shadow-md backdrop-blur-sm",
                                        spotlightConnectionId === peer.connectionId
                                            ? "bg-amber-500/80 hover:bg-amber-500"
                                            : "bg-black/50 hover:bg-black/70"
                                    )}
                                >
                                    <Pin className="h-3.5 w-3.5" />
                                </button>
                                {/* Mute is in the People pane, with a confirm. */}
                            </div>
                        ) : null}
                    </div>
                ))}
                {audioListPeers.length > 0 ? (
                    <AudioOnlyList peers={audioListPeers} health={health} activeSpeakerId={activeSpeakerId} />
                ) : null}
                </div>
                </div>
                {reaction ? (
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2">
                        <span className="animate-bounce text-6xl drop-shadow-lg">{reaction.emoji}</span>
                        {reaction.who ? (
                            <span className="rounded-full bg-black/60 px-2.5 py-0.5 text-xs">{reaction.who}</span>
                        ) : null}
                    </div>
                ) : null}
                <CaptionsOverlay
                    enabled={captionsOn}
                    speakerName={selfDisplayName}
                    incoming={incomingCaptions}
                    onFinalized={(text) => {
                        // Two-way fan-out: broadcast for live captions on
                        // peer screens, append to the persisted transcript
                        // so the post-meeting page can render the full
                        // text. Both are best-effort. Guests can't write the
                        // transcript (no token) — they still broadcast live.
                        void hub.sendCaption(text);
                        if (!isGuest) {
                            void connectParityApi.appendTranscriptLine(
                                meetingId, text, new Date().toISOString(), accessToken
                            ).catch(() => { /* ignore — captions are advisory */ });
                        }
                    }}
                />
                {statsOpen ? (
                    <CallStatsPanel
                        pcs={peerConnections.current}
                        peerLabels={Object.fromEntries(peerList.map((p) => [
                            p.connectionId, p.displayName || p.email
                        ]))}
                        onClose={() => setStatsOpen(false)}
                    />
                ) : null}
            </div>

            {confirmMuteAll ? (
                <div
                    role="alertdialog"
                    aria-label="Mute everyone"
                    className="flex shrink-0 flex-wrap items-center justify-center gap-3 border-t border-white/10 bg-zinc-900 px-4 py-2 text-sm"
                >
                    <span>Mute everyone? Each person can unmute themselves.</span>
                    <button type="button" onClick={muteAll} className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold hover:bg-red-500">
                        Mute everyone
                    </button>
                    <button type="button" onClick={() => setConfirmMuteAll(false)} className="rounded-md bg-white/10 px-3 py-1 text-xs hover:bg-white/20">
                        Cancel
                    </button>
                </div>
            ) : null}

            {confirmLeave ? (
                <div
                    role="alertdialog"
                    aria-label="Stop recording"
                    className="flex shrink-0 flex-wrap items-center justify-center gap-3 border-t border-white/10 bg-zinc-900 px-4 py-2 text-sm"
                >
                    <span>
                        You're recording. {confirmLeave === "end" ? "Ending the meeting" : "Leaving"} stops the recording
                        and saves it to your Drive, shared with the attendees.
                    </span>
                    <button
                        type="button"
                        onClick={() => {
                            const mode = confirmLeave;
                            setConfirmLeave(null);
                            if (mode === "end") endForEveryone(); else leave();
                        }}
                        className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold hover:bg-red-500"
                    >
                        {confirmLeave === "end" ? "Stop recording and end" : "Stop recording and leave"}
                    </button>
                    <button type="button" onClick={() => setConfirmLeave(null)} className="rounded-md bg-white/10 px-3 py-1 text-xs hover:bg-white/20">
                        Cancel
                    </button>
                </div>
            ) : null}

            {/* Teams' grouping: your mic, camera and screen; then raise hand,
                react, chat, people and a More menu; Leave on its own. It used
                to be 15 equal icons in one row, most of them rarely used. */}
            <footer className="flex shrink-0 flex-wrap items-center justify-center gap-1.5 border-t border-white/10 bg-zinc-900 px-2 py-2 sm:gap-2 sm:px-4 sm:py-3">
                <ToolButton onClick={toggleMic} active={!micOn} tone="danger" label={micOn ? "Mute" : "Unmute"}>
                    {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                </ToolButton>
                {!isAudio ? (
                    <ToolButton
                        onClick={toggleCamera}
                        active={!cameraOn}
                        tone="danger"
                        disabled={videoLimited && !cameraOn}
                        label={videoLimited && !cameraOn
                            ? `Video is limited to ${maxVideo} people in a call`
                            : selfOnAudio
                                ? "Stop camera (you're on audio until you speak)"
                                : cameraOn ? "Stop camera" : "Start camera"}
                    >
                        {cameraOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                    </ToolButton>
                ) : null}
                <ToolButton onClick={() => void toggleShare()} active={sharing} label={sharing ? "Stop presenting" : "Share screen"}>
                    <MonitorUp className="h-5 w-5" />
                </ToolButton>
                <span className="mx-1 hidden h-6 w-px bg-white/15 sm:block" aria-hidden />
                <ToolButton onClick={toggleHand} active={handRaised} label={handRaised ? "Lower hand" : "Raise hand"}>
                    <Hand className="h-5 w-5" />
                </ToolButton>
                <ReactionPicker onPick={(e) => void hub.sendReaction(e)} />
                <ToolButton
                    onClick={() => { setChatOpen((v) => !v); setRosterOpen(false); setInviteOpen(false); }}
                    active={chatOpen}
                    label={chatUnread > 0 && !chatOpen ? `Chat, ${chatUnread} new message${chatUnread === 1 ? "" : "s"}` : "Chat"}
                    badge={chatOpen ? 0 : chatUnread}
                >
                    <MessageSquare className="h-5 w-5" />
                </ToolButton>
                <ToolButton
                    onClick={() => { setRosterOpen((v) => !v); setChatOpen(false); setInviteOpen(false); }}
                    active={rosterOpen}
                    label="People"
                >
                    <Users className="h-5 w-5" />
                </ToolButton>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            aria-label="More actions"
                            title="More actions"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-zinc-800 text-white hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-10 sm:w-10"
                        >
                            <MoreHorizontal className="h-5 w-5" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="top" align="end" className={MENU}>
                        <DropdownMenuItem className={ITEM} onSelect={() => setDevicesOpen(true)}>
                            <Settings className="mr-2 h-4 w-4" /> Device settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        {!isAudio ? (
                            <DropdownMenuCheckboxItem className={ITEM} checked={blurOn} disabled={blurBusy} onCheckedChange={() => void toggleBlur()}>
                                {blurBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Blur background
                            </DropdownMenuCheckboxItem>
                        ) : null}
                        <DropdownMenuCheckboxItem className={ITEM} checked={noiseSuppressorOn} disabled={noiseBusy} onCheckedChange={() => void toggleNoiseSuppressor()}>
                            {noiseBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Waves className="mr-2 h-4 w-4" />} Noise suppression
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem className={ITEM} checked={captionsOn} onCheckedChange={(v) => setCaptionsOn(Boolean(v))}>
                            <CaptionsIcon className="mr-2 h-4 w-4" /> Live captions
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuItem className={ITEM} onSelect={() => { setWhiteboardOpen(true); setWbInvite(null); }}>
                            <PencilLine className="mr-2 h-4 w-4" /> Whiteboard
                        </DropdownMenuItem>
                        {isHost && !isGuest && !breakoutParentId ? (
                            <DropdownMenuItem className={ITEM} onSelect={() => window.dispatchEvent(new CustomEvent(OPEN_BREAKOUT_EVENT))}>
                                <Users className="mr-2 h-4 w-4" /> Breakout rooms
                            </DropdownMenuItem>
                        ) : null}
                        {!isGuest ? (
                            <DropdownMenuItem
                                className={ITEM}
                                onSelect={() => { setNotesOpen(true); setChatOpen(false); setRosterOpen(false); setInviteOpen(false); }}
                            >
                                <NotebookPen className="mr-2 h-4 w-4" /> Meeting notes
                            </DropdownMenuItem>
                        ) : null}
                        {!isAudio ? (
                            <DropdownMenuItem className={ITEM} onSelect={() => setSelfHidden((v) => !v)}>
                                {selfHidden ? <Video className="mr-2 h-4 w-4" /> : <VideoOff className="mr-2 h-4 w-4" />} {selfHidden ? "Show my video to me" : "Hide my video from me"}
                            </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem className={ITEM} onSelect={toggleFullscreen}>
                            {fullscreen ? <Minimize2 className="mr-2 h-4 w-4" /> : <Maximize className="mr-2 h-4 w-4" />} {fullscreen ? "Exit full screen" : "Full screen"}
                        </DropdownMenuItem>
                        {allowRecording && isHost ? (
                            <DropdownMenuItem className={ITEM} disabled={recordingBusy} onSelect={toggleRecording}>
                                {recordingBusy
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : recording
                                        ? <Square className="mr-2 h-4 w-4" />
                                        : <Circle className="mr-2 h-4 w-4 fill-red-500 text-red-500" />}
                                {recording ? "Stop recording" : "Start recording"}
                            </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuCheckboxItem className={ITEM} checked={statsOpen} onCheckedChange={(v) => setStatsOpen(Boolean(v))}>
                            <Activity className="mr-2 h-4 w-4" /> Call health
                        </DropdownMenuCheckboxItem>
                        {docPipSupported ? (
                            <DropdownMenuItem className={ITEM} onSelect={() => { if (pipWin) closePopout(); else void openPopout(false); }}>
                                <AppWindow className="mr-2 h-4 w-4" /> {pipWin ? "Close pop-out window" : "Pop out call"}
                            </DropdownMenuItem>
                        ) : !isAudio ? (
                            <>
                                <DropdownMenuItem className={ITEM} onSelect={() => void togglePip()}>
                                    <PictureInPicture2 className="mr-2 h-4 w-4" /> {pipOn ? "Exit picture-in-picture" : "Picture-in-picture"}
                                </DropdownMenuItem>
                                <p className="px-2 pb-1 text-[10px] leading-snug text-white/50">
                                    This browser can't pop out the whole call (Chrome and Edge can) — picture-in-picture shows the active speaker only.
                                </p>
                            </>
                        ) : null}
                        {docPipSupported || !isAudio ? (
                            <DropdownMenuCheckboxItem className={ITEM} checked={autoPopout} onCheckedChange={(v) => setAutoPopoutPref(Boolean(v))}>
                                <PictureInPicture2 className="mr-2 h-4 w-4" /> Show floating window when I switch tabs
                            </DropdownMenuCheckboxItem>
                        ) : null}
                        <DropdownMenuSeparator className="bg-white/10" />
                        {!isGuest ? (
                            <DropdownMenuItem className={ITEM} onSelect={() => { setInviteOpen(true); setChatOpen(false); setRosterOpen(false); }}>
                                <UserPlus className="mr-2 h-4 w-4" /> Add people
                            </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem className={ITEM} onSelect={() => void copyJoinLink()}>
                            <Link2 className="mr-2 h-4 w-4" /> Copy guest join link
                        </DropdownMenuItem>
                        {layoutMode === "together" ? (
                            <>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-white/50">Together scene</DropdownMenuLabel>
                                <DropdownMenuRadioGroup
                                    value={scene}
                                    onValueChange={(v) => {
                                        setScene(v as TogetherScene);
                                        try { window.localStorage.setItem("fg.meeting.togetherScene", v); } catch { /* not kept */ }
                                    }}
                                >
                                    {(Object.keys(TOGETHER_SCENES) as TogetherScene[]).map((k) => (
                                        <DropdownMenuRadioItem key={k} className={ITEM} value={k}>{TOGETHER_SCENES[k].label}</DropdownMenuRadioItem>
                                    ))}
                                </DropdownMenuRadioGroup>
                            </>
                        ) : null}
                        {isHost ? (
                            <>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-white/50">
                                    <LayoutGrid className="mr-1 inline h-3 w-3" /> Layout for everyone
                                </DropdownMenuLabel>
                                <DropdownMenuRadioGroup value={layoutMode} onValueChange={(v) => setLayout(v as LayoutMode)}>
                                    <DropdownMenuRadioItem className={ITEM} value="gallery">Gallery</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem className={ITEM} value="spotlight">Spotlight</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem className={ITEM} value="focus">Focus</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem className={ITEM} value="together">Together mode</DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                                <DropdownMenuItem className={ITEM} onSelect={() => void hub.setMeetingLocked(!meetingLocked)}>
                                    <Lock className="mr-2 h-4 w-4" /> {meetingLocked ? "Unlock meeting" : "Lock meeting"}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={ITEM} onSelect={() => setConfirmMuteAll(true)}>
                                    <VolumeX className="mr-2 h-4 w-4" /> Mute everyone
                                </DropdownMenuItem>
                            </>
                        ) : null}
                    </DropdownMenuContent>
                </DropdownMenu>
                <span className="mx-1 hidden h-6 w-px bg-white/15 sm:block" aria-hidden />
                <LeaveButton isHost={isHost} onLeave={() => requestLeave("leave")} onEndForEveryone={() => requestLeave("end")} />
            </footer>

            {rosterOpen ? (
                <div className="absolute bottom-16 right-0 top-12 z-20 w-full border-l border-white/10 sm:w-80">
                    <CallRoster
                        people={rosterPeople}
                        waiting={lobbyKnockers.map((k) => ({
                            connectionId: k.connectionId,
                            name: k.displayName || k.email || "Guest",
                            isGuest: k.userId.startsWith("guest:")
                        }))}
                        canAdmit={canAdmit}
                        isHost={isHost}
                        audioOnly={isAudio}
                        onAdmit={(id) => void hub.admitFromLobby(meetingId, id)}
                        onDeny={(id) => void hub.denyFromLobby(meetingId, id)}
                        onAdmitAll={admitAll}
                        onSpotlight={toggleSpotlight}
                        onMute={forceMutePeer}
                        onAddPeople={isGuest ? undefined : () => { setInviteOpen(true); setRosterOpen(false); }}
                        onLowerHand={lowerHandOf}
                        onLowerAll={isHost ? () => void hub.lowerAllHands() : undefined}
                        onClose={() => setRosterOpen(false)}
                    />
                </div>
            ) : null}

            {chatOpen ? (
                <aside
                    aria-label="Meeting chat"
                    className={cn(
                        "absolute bottom-16 right-0 top-12 z-20 flex w-full flex-col border-l border-white/10 bg-zinc-900",
                        // Narrower while a screen is being shared so the stage
                        // keeps as much width as possible.
                        shareActive ? "sm:w-72" : "sm:w-80"
                    )}
                >
                    <header className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 py-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-white/70">Meeting chat</span>
                        <button
                            type="button"
                            onClick={() => setChatOpen(false)}
                            aria-label="Close chat"
                            className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </header>
                    {(recordings.data ?? []).length > 0 ? (
                        // .dark gives the cards the dark palette inside the always-dark meeting.
                        <section aria-label="Recordings" className="dark max-h-48 shrink-0 space-y-1.5 overflow-y-auto border-b border-white/10 p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60">Recordings</div>
                            {(recordings.data ?? []).map((r) => <RecordingCard key={r.id} recording={r} />)}
                        </section>
                    ) : null}
                    {isGuest ? (
                        <>
                            <div ref={guestLogRef} role="log" aria-live="polite" className="min-h-0 flex-1 space-y-2 overflow-auto p-3 text-sm">
                                {guestChat.length === 0 ? (
                                    <p className="text-xs text-white/50">Messages sent while you're in the meeting appear here.</p>
                                ) : guestChat.map((c) => (
                                    <div key={c.id}>
                                        <div className="text-[11px] text-white/60">
                                            {c.authorDisplayName || "Someone"} · {new Date(c.sentAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                                        </div>
                                        <MessageBody html={c.bodyHtml} className="break-words [&_a]:underline" />
                                    </div>
                                ))}
                            </div>
                            <form onSubmit={sendGuestChat} className="flex gap-1 border-t border-white/10 p-2">
                                <input
                                    value={chatDraft}
                                    onChange={(e) => setChatDraft(e.target.value)}
                                    placeholder="Message everyone…"
                                    aria-label="Message everyone"
                                    maxLength={4000}
                                    className="h-8 flex-1 rounded-md border border-white/20 bg-zinc-800 px-2 text-sm outline-none focus:border-primary"
                                />
                                <button type="submit" aria-label="Send" className="flex h-8 w-8 items-center justify-center rounded-md bg-primary">
                                    <Send className="h-4 w-4" />
                                </button>
                            </form>
                        </>
                    ) : (
                        // The stored meeting chat with the group-chat composer
                        // (Markdown, emoji, @mentions, Drive files), on the dark palette.
                        <div className="dark flex min-h-0 flex-1 flex-col text-foreground">
                            <MeetingChatThread meetingId={meetingId} title={meetingSubject} variant="panel" />
                        </div>
                    )}
                </aside>
            ) : null}

            {notesOpen && !isGuest ? (
                <aside
                    aria-label="Meeting notes"
                    className={cn(
                        "absolute bottom-16 right-0 top-12 z-20 flex w-full flex-col border-l border-white/10 bg-zinc-900",
                        shareActive ? "sm:w-80" : "sm:w-96"
                    )}
                >
                    <header className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 py-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-white/70">Meeting notes</span>
                        <button
                            type="button"
                            onClick={() => setNotesOpen(false)}
                            aria-label="Close meeting notes"
                            className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </header>
                    {/* .dark: the app's dark palette inside the always-dark meeting. */}
                    <div className="dark min-h-0 flex-1 overflow-y-auto p-3 text-foreground">
                        <MeetingNotes meetingId={meetingId} hub={hub} />
                    </div>
                </aside>
            ) : null}

            {/* Invite panel */}
            {inviteOpen ? (
                <aside className="absolute left-0 top-12 bottom-16 flex w-72 flex-col border-r border-white/10 bg-zinc-900">
                    <header className="flex items-center justify-between border-b border-white/10 px-3 py-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-white/70">Add participant</span>
                        <button type="button" onClick={() => setInviteOpen(false)} className="rounded p-0.5 hover:bg-white/10">
                            <X className="h-4 w-4" />
                        </button>
                    </header>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        <GuestLinkButton meetingId={meetingId} />
                        <div className="flex items-center gap-2 py-1 text-[10px] uppercase tracking-wider text-white/40">
                            <span className="h-px flex-1 bg-white/10" /> or invite by name <span className="h-px flex-1 bg-white/10" />
                        </div>
                        <input
                            value={inviteQuery}
                            onChange={(e) => setInviteQuery(e.target.value)}
                            placeholder="Search by name or email…"
                            className="w-full h-8 rounded-md border border-white/20 bg-zinc-800 px-2 text-sm outline-none focus:border-primary"
                        />
                        {inviteSuggestions.length > 0 && (
                            <ul className="rounded-md border border-white/10 bg-zinc-800 divide-y divide-white/10 text-sm">
                                {inviteSuggestions.map((u) => (
                                    <li key={u.userId}>
                                        <button
                                            type="button"
                                            disabled={inviteStatus === "sending"}
                                            onClick={() => void inviteParticipant(u.email)}
                                            className="flex w-full items-center gap-2 px-3 py-2 hover:bg-white/10 disabled:opacity-50"
                                        >
                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-600 text-xs font-semibold">
                                                {(u.displayName ?? u.email).charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0 text-left">
                                                <div className="truncate font-medium">{u.displayName ?? u.email}</div>
                                                {u.displayName && <div className="truncate text-xs text-white/50">{u.email}</div>}
                                            </div>
                                            {inviteStatus === "sending" ? (
                                                <Loader2 className="ml-auto h-3.5 w-3.5 shrink-0 animate-spin" />
                                            ) : null}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {/* Manual email entry when no suggestions */}
                        {inviteSuggestions.length === 0 && inviteQuery.includes("@") && (
                            <button
                                type="button"
                                disabled={inviteStatus === "sending"}
                                onClick={() => void inviteParticipant(inviteQuery.trim())}
                                className="flex w-full items-center gap-2 rounded-md border border-white/20 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-50"
                            >
                                {inviteStatus === "sending" ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <UserPlus className="h-4 w-4" />
                                )}
                                Invite {inviteQuery.trim()}
                            </button>
                        )}
                        {inviteStatus === "sent" && (
                            <p className="flex items-center gap-1.5 text-sm text-emerald-400">
                                <Check className="h-4 w-4" /> Invited! They'll see an incoming call.
                            </p>
                        )}
                        {inviteStatus === "error" && (
                            <p className="text-sm text-red-400">Failed to send invite. Please try again.</p>
                        )}
                    </div>
                </aside>
            ) : null}

            {!isGuest ? (
                <BreakoutController
                    key={meetingId}
                    meetingId={meetingId}
                    parentId={breakoutParentId}
                    isHost={isHost}
                    people={peerList.map((p) => ({ userId: p.userId, displayName: p.displayName }))}
                    kind={kind}
                />
            ) : null}

            {/* Lobby admit panel — shown to host (and all participants) when someone knocks */}
            {lobbyKnockers.length > 0 && canAdmit && !rosterOpen ? (
                <div className="absolute left-4 top-14 z-30 flex flex-col gap-2 w-72">
                    {lobbyKnockers.map((k) => (
                        <div
                            key={k.connectionId}
                            className="flex items-center gap-3 rounded-lg border border-white/10 bg-zinc-900/90 px-3 py-2.5 shadow-xl backdrop-blur-sm text-sm"
                        >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold">
                                {(k.displayName || k.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">{k.displayName || k.email}</div>
                                <div className="text-[11px] text-white/50">wants to join</div>
                            </div>
                            {canAdmit ? (
                                <div className="flex gap-1 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            void hub.admitFromLobby(meetingId, k.connectionId);
                                            setLobbyKnockers((prev) => prev.filter((x) => x.connectionId !== k.connectionId));
                                        }}
                                        className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                                    >
                                        Admit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            void hub.denyFromLobby(meetingId, k.connectionId);
                                            setLobbyKnockers((prev) => prev.filter((x) => x.connectionId !== k.connectionId));
                                        }}
                                        className="rounded-md bg-zinc-700 px-2 py-1 text-xs font-medium text-white hover:bg-zinc-600"
                                    >
                                        Deny
                                    </button>
                                </div>
                            ) : (
                                <div className="text-[10px] text-white/40 shrink-0">Waiting</div>
                            )}
                        </div>
                    ))}
                </div>
            ) : null}

            {/* Auto-end overlay — shown when last participant triggered the call end */}
            {autoEndMsg && !outgoing ? (
                <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="flex flex-col items-center gap-3 rounded-xl bg-zinc-800 px-8 py-6 text-center shadow-2xl">
                        <PhoneOff className="h-10 w-10 text-red-400" />
                        <p className="text-lg font-semibold text-white">{autoEndMsg}</p>
                    </div>
                </div>
            ) : null}

            {whiteboardOpen ? (
                <MeetingWhiteboard
                    meetingId={meetingId}
                    meetingSubject={meetingSubject}
                    hub={hub}
                    isGuest={isGuest}
                    onClose={() => setWhiteboardOpen(false)}
                />
            ) : null}

            {outgoing ? (
                <OutgoingCallScreen
                    phase={outgoing}
                    calleeLabel={calleeLabel ?? meetingSubject}
                    audioOnly={isAudio}
                    onCancel={leave}
                    onRetry={() => void retryCall()}
                    onClose={leave}
                />
            ) : null}

            {/* Everyone's audio plays from here — independent of the layout, the share stage, a camera being
                off, or the pop-out. It used to come out of each tile's <video>, which isn't rendered while that
                person's camera is off: someone with their camera off could not be heard at all. */}
            <div className="hidden" aria-hidden>
                {peerList.map((p) => (p.stream
                    ? <RemoteAudio key={`${p.clientId}:${p.stream.id}`} stream={p.stream} onBlocked={onAudioBlocked} />
                    : null))}
            </div>

            {pipWin ? (
                <MeetingPopout
                    win={pipWin}
                    subject={meetingSubject}
                    main={popoutTiles.main}
                    others={popoutTiles.others}
                    presenting={sharing
                        ? "You are presenting"
                        : sharingPeer ? `${sharingPeer.displayName || sharingPeer.email || "Someone"} is presenting` : null}
                    audioOnly={isAudio}
                    micOn={micOn}
                    camOn={cameraOn}
                    camDisabled={videoLimited}
                    sharing={sharing}
                    handRaised={handRaised}
                    reconnecting={reconnecting}
                    onToggleMic={toggleMic}
                    onToggleCam={toggleCamera}
                    onToggleShare={() => void toggleShare()}
                    onToggleHand={toggleHand}
                    onLeave={() => { window.focus(); closePopout(); requestLeave("leave"); }}
                    onBackToTab={() => { window.focus(); closePopout(); }}
                />
            ) : null}

            <DeviceSettingsDialog
                open={devicesOpen}
                onOpenChange={setDevicesOpen}
                audioOnly={isAudio}
                cameraId={activeDevices.cameraId}
                micId={activeDevices.micId}
                speakerId={activeDevices.speakerId}
                onSelect={(k, id) => void switchDevice(k, id)}
            />
        </div>
        </SpeakerContext.Provider>
    );
}

/** "<Meeting subject> — Recording 2026-09-13 14-05.webm" (no characters Windows or Drive would reject). */
function recordingFileName(subject: string, at: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    const stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}-${pad(at.getMinutes())}`;
    const safe = subject.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 120) || "Meeting";
    return `${safe} — Recording ${stamp}.webm`;
}

/**
 * Elapsed time in the call, as Teams shows beside the title — plus, for a
 * scheduled meeting, how long is left once the end is within five minutes.
 */
function CallTimer({ scheduledEnd }: { scheduledEnd?: string | null }) {
    const [start] = useState(() => Date.now());
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, []);
    const s = Math.max(0, Math.floor((now - start) / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const text = h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
        : `${m}:${String(sec).padStart(2, "0")}`;
    const end = scheduledEnd ? Date.parse(scheduledEnd) : NaN;
    const left = Number.isFinite(end) ? end - now : NaN;
    const soon = Number.isFinite(left) && left > 0 && left <= 5 * 60_000;
    const endLabel = Number.isFinite(end)
        ? `Scheduled to end at ${new Date(end).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`
        : undefined;
    return (
        <span className="mr-1 flex items-center gap-1.5 text-xs tabular-nums text-white/60" title={endLabel}>
            <span aria-label={`Call duration ${text}`}>{text}</span>
            {soon ? (
                <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
                    {Math.max(1, Math.ceil(left / 60_000))} min left
                </span>
            ) : null}
        </span>
    );
}

/** Leave; the host can also end the meeting for everyone. */
function LeaveButton({ isHost, onLeave, onEndForEveryone }: {
    isHost: boolean;
    onLeave: () => void;
    onEndForEveryone: () => void;
}) {
    const base = "flex h-10 items-center gap-1 bg-destructive text-sm font-semibold text-destructive-foreground hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white";
    if (!isHost) {
        return (
            <button type="button" onClick={onLeave} className={cn(base, "rounded-md px-4")}>
                <PhoneOff className="h-4 w-4" /> Leave
            </button>
        );
    }
    return (
        <div className="flex">
            <button type="button" onClick={onLeave} className={cn(base, "rounded-l-md pl-4 pr-3")}>
                <PhoneOff className="h-4 w-4" /> Leave
            </button>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button type="button" aria-label="More ways to leave" className={cn(base, "rounded-r-md border-l border-black/20 px-2")}>
                        <ChevronDown className="h-4 w-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="end" className={MENU}>
                    <DropdownMenuItem className={ITEM} onSelect={onLeave}>Leave — the meeting carries on</DropdownMenuItem>
                    <DropdownMenuItem className={cn(ITEM, "text-red-300 focus:text-red-200")} onSelect={onEndForEveryone}>
                        End meeting for everyone
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

/**
 * A participant's <video>, with two things the plain element got wrong.
 *
 * 1. It binds the stream from a CALLBACK ref, not an effect keyed on the
 *    stream. Tile unmounts its children while a camera is off, so the element
 *    is destroyed and recreated — an effect keyed on the (unchanged) stream
 *    never re-ran, and the tile stayed black after someone turned their camera
 *    back on. A callback ref fires on every mount, so it can't drift.
 *
 * 2. It picks object-fit from the stream's real aspect ratio. Phones send
 *    portrait video; forcing object-cover into a landscape tile cropped the
 *    sides away and blew the subject up to a face-filling close-up. Portrait
 *    sources are letterboxed with object-contain instead, which is what Meet
 *    and Teams do. Landscape keeps object-cover so desktop tiles stay full.
 */
function VideoSurface({ stream, muted, pipTarget, peerId }: {
    stream: MediaStream | null;
    muted?: boolean;
    pipTarget?: boolean;
    /** Lets the picture-in-picture fallback find the active speaker's video. */
    peerId?: string;
}) {
    const [portrait, setPortrait] = useState(false);
    const sinkId = useContext(SpeakerContext);

    const bind = useCallback((el: HTMLVideoElement | null) => {
        if (!el) return;
        if (stream && el.srcObject !== stream) el.srcObject = stream;
        if (!muted) applySink(el, sinkId);

        const measure = () => {
            if (!el.videoWidth || !el.videoHeight) return;
            setPortrait(el.videoHeight > el.videoWidth);
        };
        measure();
        el.addEventListener("loadedmetadata", measure);
        // A phone rotating mid-call changes the track's dimensions without
        // re-firing loadedmetadata, so watch for resize too.
        el.addEventListener("resize", measure);
    }, [stream, sinkId, muted]);

    return (
        <video
            ref={bind}
            autoPlay
            playsInline
            muted={muted}
            data-peer-video={peerId}
            {...(pipTarget ? { "data-pip-target": true } : {})}
            className={cn("h-full w-full", portrait ? "object-contain" : "object-cover")}
        />
    );
}

function LocalTile({ stream, micOn, cameraOn, handRaised, sharing, audioOnly, compact }: {
    stream: MediaStream | null;
    micOn: boolean;
    cameraOn: boolean;
    handRaised: boolean;
    sharing: boolean;
    audioOnly?: boolean;
    /** In the presenting strip: sized by the strip's width. */
    compact?: boolean;
}) {
    if (audioOnly) {
        return <AudioAvatar label="You" muted={!micOn} stream={stream} />;
    }
    return (
        <Tile label="You" handRaised={handRaised} sharing={sharing} muted={!micOn} noVideo={!cameraOn} stream={stream} compact={compact}>
            <VideoSurface stream={stream} muted />
        </Tile>
    );
}

function RemoteTile({ peer, audioOnly, compact, hideVideo, health, speaking }: {
    peer: Peer;
    audioOnly?: boolean;
    compact?: boolean;
    /** Show the avatar instead of their video (an older presenter's video IS the screen). */
    hideVideo?: boolean;
    health?: PeerHealth;
    speaking?: boolean;
}) {
    const label = peer.displayName || peer.email;
    const status = <PeerSignal health={health} expectAudio={peer.audio && !peer.reconnecting} reconnecting={peer.reconnecting} />;
    if (audioOnly) {
        return <AudioAvatar label={label} muted={!peer.audio} stream={peer.stream} status={status} speaking={speaking} />;
    }
    return (
        <Tile
            label={label}
            handRaised={peer.handRaised}
            sharing={peer.sharing}
            muted={!peer.audio}
            noVideo={hideVideo || !peer.video || !peer.stream}
            stream={peer.stream}
            compact={compact}
            speaking={speaking}
            reconnecting={peer.reconnecting}
            status={status}
        >
            {/* Muted: their audio plays from RemoteAudio, so it keeps playing whatever happens to this tile. */}
            <VideoSurface stream={peer.stream} muted pipTarget peerId={peer.connectionId} />
        </Tile>
    );
}

/**
 * Full-bleed presentation surface shown while someone shares a screen.
 * object-contain (not cover) so slides/IDE windows are never cropped. Always
 * muted: the presenter's voice plays from RemoteAudio like everyone else's.
 */
function ShareStage({ stream, label, isSelf, onStop }: {
    stream: MediaStream | null;
    label: string;
    isSelf: boolean;
    onStop?: () => void;
}) {
    const ref = useRef<HTMLVideoElement>(null);
    // Your own share is NOT previewed by default (Teams does the same): if the shared screen or window
    // shows this meeting, a live preview films itself — the endless "mirror" tunnel.
    const [showPreview, setShowPreview] = useState(false);
    const renderVideo = stream !== null && (!isSelf || showPreview);
    useEffect(() => {
        if (ref.current && stream && renderVideo) ref.current.srcObject = stream;
    }, [stream, renderVideo]);
    return (
        <div className={cn(
            "relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-lg border bg-black",
            isSelf ? "border-red-500/70" : "border-white/10"
        )}>
            {isSelf && !showPreview ? (
                <div className="flex max-w-sm flex-col items-center gap-3 px-6 text-center text-white">
                    <MonitorUp className="h-10 w-10 text-primary" />
                    <div className="text-base font-semibold">You're presenting</div>
                    <p className="text-xs text-white/60">
                        Everyone can see your screen. Your own preview is hidden so the meeting doesn't mirror itself.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                        {onStop ? (
                            <button
                                type="button"
                                onClick={onStop}
                                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold hover:bg-red-500"
                            >
                                Stop presenting
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => setShowPreview(true)}
                            className="rounded-md border border-white/20 px-3 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10"
                        >
                            Show preview
                        </button>
                    </div>
                </div>
            ) : renderVideo ? (
                <video
                    ref={ref}
                    autoPlay
                    playsInline
                    muted
                    data-pip-target={isSelf ? undefined : true}
                    className="h-full w-full object-contain"
                />
            ) : (
                <div className="flex flex-col items-center gap-2 text-white/60">
                    <MonitorUp className="h-8 w-8" />
                    <span className="text-sm">{label}</span>
                </div>
            )}
            <span className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                <MonitorUp className="h-3.5 w-3.5 text-primary" /> {label}
            </span>
            {isSelf && showPreview ? (
                <button
                    type="button"
                    onClick={() => setShowPreview(false)}
                    className="absolute right-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm hover:bg-black/80"
                >
                    Hide preview
                </button>
            ) : null}
        </div>
    );
}

/**
 * Everyone outside the video set (FPT-7): heard, not seen. One compact row per person with a live level meter;
 * the ring lights for whoever is speaking. They rotate into the video tiles by speaking.
 */
function AudioOnlyList({ peers, health, activeSpeakerId }: {
    peers: Peer[];
    health: Record<string, PeerHealth>;
    activeSpeakerId: string | null;
}) {
    return (
        <div className="col-span-full flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-zinc-900/60 p-2">
            <span className="px-1 text-xs text-white/60">On audio ({peers.length})</span>
            <ul aria-label="On audio" className="contents">
                {peers.map((p) => {
                    const label = p.displayName || p.email || "Participant";
                    const speaking = p.audio && (p.connectionId === activeSpeakerId || (health[p.connectionId]?.audioLevel ?? 0) > 0.04);
                    return (
                        <li
                            key={p.connectionId}
                            title={label}
                            className={cn(
                                "flex min-w-0 max-w-[12rem] items-center gap-1.5 rounded-full bg-zinc-800 py-0.5 pl-0.5 pr-2 text-xs ring-1",
                                speaking ? "ring-emerald-400" : "ring-white/10",
                                p.reconnecting && "opacity-60"
                            )}
                        >
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-[11px] font-semibold">
                                {label.charAt(0).toUpperCase()}
                            </span>
                            <span className="truncate">{label}</span>
                            {p.audio
                                ? <AudioLevelMeter stream={p.stream} bars={3} className="shrink-0 text-emerald-400" />
                                : <MicOff className="h-3 w-3 shrink-0 text-red-400" aria-label="Muted" />}
                            {p.handRaised ? <Hand className="h-3 w-3 shrink-0 text-amber-300" aria-label="Hand raised" /> : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function AudioAvatar({ label, muted, stream, status, speaking }: {
    label: string;
    muted: boolean;
    stream?: MediaStream | null;
    /** Connection / audio diagnostics (remote participants). */
    status?: React.ReactNode;
    speaking?: boolean;
}) {
    // Picture only. Everyone's audio plays from the room's RemoteAudio elements; this used to play its own
    // copy (and your own tile once played your microphone back to you).
    return (
        <div className="flex flex-col items-center gap-2">
            <div className={cn(
                "relative flex h-20 w-20 items-center justify-center rounded-full bg-zinc-700 text-2xl font-semibold ring-2",
                speaking ? "ring-emerald-400" : "ring-white/10"
            )}>
                {label.charAt(0).toUpperCase()}
                {/* Audio level bars inside avatar ring */}
                {!muted && stream ? (
                    <AudioLevelMeter
                        stream={stream}
                        muted={muted}
                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-emerald-400"
                    />
                ) : null}
            </div>
            <span className="flex max-w-[140px] items-center gap-1 text-xs text-white/80">
                <span className="truncate">{label}</span>
                {status}
            </span>
            {muted ? <MicOff className="h-3.5 w-3.5 text-red-400" aria-label="Muted" /> : null}
        </div>
    );
}

/** A tile pinned for this viewer only: first in the grid and across it. */
const PINNED_TILE = "order-first sm:col-span-2 lg:col-span-3 [&_[data-tile]]:mx-auto [&_[data-tile]]:max-h-[55vh]";

/** "Pin for me": shows on hover; unlike the host's spotlight it changes nobody else's view. */
function PinForMe({ pinned, onToggle, offset }: { pinned: boolean; onToggle: () => void; offset: boolean }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-pressed={pinned}
            aria-label={pinned ? "Unpin from my view" : "Pin to my view"}
            title={pinned ? "Unpin (only you see this)" : "Pin to my view (only you see this)"}
            className={cn(
                "absolute top-2 z-10 flex h-7 w-7 items-center justify-center rounded-md text-white shadow-md backdrop-blur-sm focus-visible:flex group-hover:flex",
                offset ? "right-11" : "right-2",
                pinned ? "flex bg-sky-600/90 hover:bg-sky-600" : "hidden bg-black/50 hover:bg-black/70"
            )}
        >
            <Pin className="h-3.5 w-3.5" />
        </button>
    );
}

function Tile({
    label, children, handRaised, sharing, muted, noVideo, stream, compact, speaking, reconnecting, status
}: {
    label: string;
    children: React.ReactNode;
    handRaised: boolean;
    sharing: boolean;
    muted: boolean;
    noVideo: boolean;
    stream?: MediaStream | null;
    /** In the presenting strip: width-driven, no minimum height. */
    compact?: boolean;
    speaking?: boolean;
    reconnecting?: boolean;
    /** Connection / audio diagnostics, next to the name. */
    status?: React.ReactNode;
}) {
    return (
        <div data-tile className={cn(
            // aspect-video gives every tile a deterministic height. Without it
            // the grid rows sized to a 140px minimum and a phone in portrait
            // got a letterbox sliver of video. In the presenting strip the
            // strip's width decides — a 140px minimum there made the tile
            // wider than the strip (the old horizontal scrollbar).
            "relative flex aspect-video items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-zinc-800",
            compact ? "w-full" : "min-h-[140px]",
            sharing ? "ring-2 ring-primary" : speaking && "ring-2 ring-emerald-400"
        )}>
            {noVideo ? (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-700 text-lg font-semibold">
                    {label.charAt(0).toUpperCase()}
                </div>
            ) : children}
            {reconnecting ? (
                <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/60 text-xs text-amber-200">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Reconnecting…
                </div>
            ) : null}
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{label}</span>
                    {status}
                    {/* Audio level meter — small animated bars next to name */}
                    <AudioLevelMeter
                        stream={stream}
                        muted={muted}
                        bars={3}
                        className="shrink-0 text-emerald-400"
                    />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {muted ? <MicOff className="h-3.5 w-3.5 text-red-400" aria-label="Muted" /> : null}
                    {handRaised ? <Hand className="h-3.5 w-3.5 text-amber-300" aria-label="Hand raised" /> : null}
                    {sharing ? <MonitorUp className="h-3.5 w-3.5 text-primary" aria-label="Sharing" /> : null}
                </div>
            </div>
        </div>
    );
}

/**
 * Whose media is flowing: a small signal icon per remote participant (connection state), turning into a
 * "no audio" warning when they're unmuted and connected but none of their audio is reaching us — the
 * one-way-audio case. The tooltip spells out both directions.
 */
function PeerSignal({ health, expectAudio, reconnecting }: {
    health?: PeerHealth;
    expectAudio: boolean;
    reconnecting: boolean;
}) {
    const state = health?.state ?? "new";
    const silentFor = health ? Date.now() - health.lastAudioAt : Infinity;
    const noAudio = expectAudio && state === "connected" && silentFor > 4000;
    let icon: React.ReactNode;
    let tone: string;
    let text: string;
    if (reconnecting) {
        icon = <Loader2 className="h-3 w-3 animate-spin" />;
        tone = "text-amber-300";
        text = "Reconnecting…";
    } else if (state === "failed" || state === "closed") {
        icon = <WifiOff className="h-3 w-3" />;
        tone = "text-red-400";
        text = "Media connection failed — retrying";
    } else if (state !== "connected") {
        icon = <SignalLow className="h-3 w-3" />;
        tone = "text-amber-300";
        text = state === "disconnected" ? "Connection interrupted — recovering" : "Connecting media…";
    } else if (noAudio) {
        icon = <VolumeX className="h-3 w-3" />;
        tone = "text-amber-300";
        text = "Connected, but none of their audio is reaching you";
    } else {
        icon = <SignalHigh className="h-3 w-3" />;
        tone = "text-emerald-400";
        text = "Connected";
    }
    const detail = health
        ? ` · Their audio: ${health.receivingAudio ? `arriving (level ${Math.round(health.audioLevel * 100)}%)` : "not arriving"}`
            + ` · Your audio to them: ${health.sendingAudio ? "sending" : "not sending"}`
        : "";
    return (
        <span role="img" aria-label={text + detail} title={text + detail} className={cn("inline-flex shrink-0 items-center", tone)}>
            {icon}
        </span>
    );
}

/**
 * Plays one remote participant's audio. Detects the autoplay block (play() rejected with
 * NotAllowedError) so the room can offer "Click to enable audio" instead of silently playing nothing.
 */
function RemoteAudio({ stream, onBlocked }: { stream: MediaStream; onBlocked: () => void }) {
    const sinkId = useContext(SpeakerContext);
    const bind = useCallback((el: HTMLAudioElement | null) => {
        if (!el) return;
        if (el.srcObject !== stream) el.srcObject = stream;
        applySink(el, sinkId);
        el.play().catch((err: unknown) => {
            if (err instanceof DOMException && err.name === "NotAllowedError") onBlocked();
        });
    }, [stream, sinkId, onBlocked]);
    return <audio ref={bind} autoPlay data-remote-audio />;
}

/**
 * Copies a public /guest/:meetingId link that lets anyone — including people
 * without an Fowlguard account — join this meeting straight from a browser.
 */
function GuestLinkButton({ meetingId }: { meetingId: string }) {
    const [copied, setCopied] = useState(false);
    const link = `${window.location.origin}/guest/${meetingId}`;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard blocked — user can still read the link below */ }
    };
    return (
        <div className="rounded-md border border-white/10 bg-zinc-800/60 p-2">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-white/80">
                <UserPlus className="h-3.5 w-3.5" /> Invite external guests
            </div>
            <p className="mb-2 text-[11px] text-white/50">
                Anyone with this link can join — no account needed.
            </p>
            <div className="flex gap-1">
                <input
                    readOnly
                    value={link}
                    onFocus={(e) => e.currentTarget.select()}
                    className="h-8 min-w-0 flex-1 rounded-md border border-white/20 bg-zinc-900 px-2 text-xs text-white/80 outline-none"
                />
                <button
                    type="button"
                    onClick={() => void copy()}
                    className="flex h-8 shrink-0 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground"
                >
                    {copied ? <Check className="h-3.5 w-3.5" /> : null}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </div>
    );
}

const REACTIONS = ["👍", "❤️", "😂", "🎉", "👏", "🙏"];

function ReactionPicker({ onPick }: { onPick: (emoji: string) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <ToolButton onClick={() => setOpen((v) => !v)} label="React">
                <Smile className="h-5 w-5" />
            </ToolButton>
            {open ? (
                <div className="absolute bottom-full left-0 mb-1 flex gap-0.5 rounded-md border border-white/10 bg-zinc-800 p-1 shadow-lg">
                    {REACTIONS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            onClick={() => { onPick(emoji); setOpen(false); }}
                            className="rounded px-1 text-lg hover:bg-white/10"
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function ToolButton({
    children, onClick, active, label, tone = "on", disabled, badge = 0
}: {
    children: React.ReactNode;
    onClick: () => void;
    active?: boolean;
    label: string;
    /**
     * "danger": red while active (mic or camera off). "on": highlighted while
     * active (chat open, hand raised). Everything used to turn red.
     */
    tone?: "on" | "danger";
    disabled?: boolean;
    /** A count in the corner (unread chat messages); hidden at 0. */
    badge?: number;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            title={label}
            className={cn(
                "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 sm:h-10 sm:w-10",
                active
                    ? tone === "danger" ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"
                    : "bg-zinc-800 text-white hover:bg-zinc-700"
            )}
        >
            {children}
            {badge > 0 ? (
                <span
                    className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold leading-none text-white"
                    aria-hidden
                >
                    {badge > 99 ? "99+" : badge}
                </span>
            ) : null}
        </button>
    );
}
